import {
  AlertTriangle,
  Ban,
  Bell,
  BookOpen,
  Check,
  Clock,
  createElement,
  Database,
  FileText,
  LogOut,
  Play,
  RefreshCcw,
  RotateCcw,
  Search,
  Server,
  Settings,
  Trash2,
  Upload,
  UserPlus,
  Volume2,
  VolumeX
} from "lucide";
import { AdminAPI } from "./api";
import type {
  AdminArtifactSummary,
  AdminNotification,
  AdminOverview,
  AdmissionStats,
  AuthSessionUser,
  BulkJob,
  BulkJobItem,
  BulkOrganizeResponse,
  BulkSourceCandidate,
  CreateLibraryRequest,
  DeadLetter,
  IngestAdmission,
  LibraryProfile,
  LibraryProfilePatch,
  LibrarySourcePatch,
  LibraryVersion,
  LibraryVersionCandidate,
  ManagedSource,
  NotificationCounts,
  QuarantineLLMSummary,
  QuarantineReport,
  QuarantineRow,
  PublishedAggregate,
  RunEvent,
  RunState,
  SourceInspection,
  SourceRecord,
  SourceType,
  StageName,
  SubmitSourceRequest,
  UsageAdminReport,
  UsageEventsReport,
  UsageMissesReport,
  UserRequest
} from "./types";
import { renderUsageLimits, renderUsageReport, usagePolicyFromNode, type UsageDetailKey, type UsageMode } from "./usage";
import { routeForView, viewFromPathname, type AdminView } from "./router";
import "./styles.css";

type ViewName = AdminView;
type LibraryTab = "overview" | "configuration" | "sources" | "aggregate" | "versions" | "indexing" | "preview" | "quality" | "metrics";
type LibraryWorkspace = "catalog" | "add" | "bulk" | "bulkJobs";
type RunTab = "events" | "diagnostics" | "artifacts" | "crawl" | "quarantine";
type AuthMode = "login" | "register";
type RefreshTier = "high" | "normal" | "low";

const stages: StageName[] = ["discover", "parse", "enrich", "embed", "score", "publish", "cleanup"];
const refreshTierLabels: Record<RefreshTier, string> = {
  high: "Hot",
  normal: "Warm",
  low: "Cold"
};
const liveRefreshIntervalMs = 5000;
const api = new AdminAPI();

const state = {
  baseURL: import.meta.env.VITE_OZ_API_URL || defaultAPIBaseURL(),
  session: null as AuthSessionUser | null,
  authMode: "login" as AuthMode,
  view: viewFromPathname(window.location.pathname) as ViewName,
  libraries: [] as LibraryProfile[],
  claimRequests: [] as UserRequest[],
  libraryRequests: [] as UserRequest[],
  supportIssues: [] as UserRequest[],
  sources: [] as SourceRecord[],
  admissions: [] as IngestAdmission[],
  admissionStats: null as AdmissionStats | null,
  admissionStatusFilter: "",
  admissionIntentFilter: "",
  admissionUrgencyFilter: "",
  systemConfig: null as Record<string, unknown> | null,
  refreshTierSearch: "",
  overview: null as AdminOverview | null,
  usage: null as UsageAdminReport | null,
  usageDetail: null as UsageDetailKey,
  usageEvents: null as UsageEventsReport | null,
  usageEventsLoading: false,
  usageMisses: null as UsageMissesReport | null,
  usageMissesLoading: false,
  usageMode: "activeUsers" as UsageMode,
  usageSearch: "",
  usageUserPage: 1,
  usageLibraryPage: 1,
  usagePageSize: 8,
  selectedLibrary: "",
  libraryTab: "overview" as LibraryTab,
  libraryWorkspace: "catalog" as LibraryWorkspace,
  librarySearch: "",
  libraryStatusFilter: "",
  selectedRun: "",
  selectedRunIDs: new Set<string>(),
  sourceInspection: null as SourceInspection | null,
  bulkResult: null as BulkOrganizeResponse | null,
  bulkJobs: [] as BulkJob[],
  bulkDetailPage: 1,
  bulkDetailStatus: "",
  bulkDetailQuery: "",
  selectedTab: "events" as RunTab,
  quarantineSelection: new Set<string>(),
  modalOnClose: null as null | (() => void),
  eventStream: null as EventSource | null,
  logEvents: [] as RunEvent[],
  selectedLogEventID: "",
  logStream: null as EventSource | null,
  notifications: [] as AdminNotification[],
  notificationCounts: { total: 0, unread: 0, bySeverity: {} } as NotificationCounts,
  notificationStream: null as EventSource | null,
  notificationSound: localStorage.getItem("oz_admin_notification_sound") === "true",
  seenNotificationIds: new Set<string>()
};

api.setBaseURL(state.baseURL);

let liveRefreshTimer: number | null = null;
let liveRefreshInFlight = false;

function redirectToConfiguredLoopbackHost() {
  if (!import.meta.env.VITE_OZ_API_URL) return false;
  let apiURL: URL;
  try {
    apiURL = new URL(import.meta.env.VITE_OZ_API_URL);
  } catch {
    return false;
  }
  const pageHost = window.location.hostname;
  const apiHost = apiURL.hostname;
  const loopbackPair =
    (pageHost === "127.0.0.1" && apiHost === "localhost") ||
    (pageHost === "localhost" && apiHost === "127.0.0.1");
  if (!loopbackPair) return false;
  const target = new URL(window.location.href);
  target.hostname = apiHost;
  window.location.replace(target.toString());
  return true;
}

function defaultAPIBaseURL() {
  if (window.location.hostname === "admin.tryoz.dev") return window.location.origin;
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    return `${window.location.protocol}//${window.location.hostname}:8080`;
  }
  return "http://localhost:8080";
}

const sampleSource: SubmitSourceRequest = {
  source: {
    source_id: "src_vercel_next",
    tenant_id: "public",
    source_type: "github_repo",
    url: "https://github.com/vercel/next.js",
    library_id: "/vercel/next.js",
    visibility: "public",
    auth_ref: "",
    crawl_scope: {
      include: ["docs/**", "README.md"],
      exclude: ["src/**", "test/**", "node_modules/**"]
    },
    version_policy: {
      default_branch: "canary",
      tags: [],
      semver: true
    },
    refresh_policy: {
      mode: "webhook_plus_periodic",
      priority: "high"
    }
  }
};

document.querySelector<HTMLDivElement>("#app")!.innerHTML = `
  <div id="auth-screen" class="auth-screen">
    <section class="auth-panel">
      <div class="brand auth-brand">
        <strong>Oz</strong>
        <span>Admin</span>
      </div>
      <h1 id="auth-title">Admin sign in</h1>
      <p class="auth-note">Only @hiringbae.com email addresses can create admin accounts.</p>
      <form id="auth-form" class="auth-form">
        <label>Email<input id="auth-email" type="email" autocomplete="email" placeholder="you@hiringbae.com" required /></label>
        <label>Password<input id="auth-password" type="password" autocomplete="current-password" required /></label>
        <label id="auth-display-row" class="hidden">Display name<input id="auth-display-name" autocomplete="name" /></label>
        <div class="auth-actions">
          <button id="auth-submit" class="primary" type="submit" data-icon="check">Sign in</button>
          <button id="auth-switch" type="button" data-icon="userplus">Create account</button>
        </div>
        <p id="auth-message" class="auth-message"></p>
      </form>
    </section>
  </div>

  <div id="shell" class="shell hidden">
    <aside class="sidebar">
      <div class="brand">
        <strong>Oz</strong>
        <span>Admin</span>
      </div>
      <nav>
        ${navButton("libraries", "Libraries", "book")}
        ${navButton("claims", "Claim Requests", "check")}
        ${navButton("requests", "Requested Libraries", "upload")}
        ${navButton("support", "Support Issues", "alert")}
        ${navButton("sources", "Sources", "upload")}
        ${navButton("refreshTiers", "Refresh Tiers", "clock")}
        ${navButton("queue", "Queue", "server")}
        ${navButton("runs", "Runs", "clock")}
        ${navButton("usage", "Usage", "database")}
        ${navButton("limits", "Limits", "settings")}
        ${navButton("logs", "Logs", "file")}
        ${navButton("notifications", "Notifications", "bell")}
        ${navButton("dead", "Dead letters", "alert")}
        ${navButton("system", "System", "settings")}
      </nav>
    </aside>

    <main class="main">
      <header class="topbar">
        <div class="account">
          <span id="account-email"></span>
          <button id="logout" type="button" data-icon="logout">Sign out</button>
        </div>
        <button id="refresh" type="button" data-icon="refresh">Refresh</button>
        <button id="notification-sound" type="button" data-icon="${state.notificationSound ? "volume2" : "volumex"}">${state.notificationSound ? "Sound on" : "Sound off"}</button>
      </header>

      <section id="admin-overview-strip" class="overview-strip"></section>

      <section id="view-libraries" class="view active">
        <div class="page-toolbar">
          <div>
            <h1>Libraries</h1>
            <p id="library-count-summary" class="muted">No libraries loaded.</p>
          </div>
          <div class="toolbar-actions">
            <button id="reload-libraries" type="button" data-icon="refresh">Reload</button>
            ${libraryWorkspaceButton("catalog", "Catalog", "book")}
            ${libraryWorkspaceButton("add", "Add source", "upload")}
            ${libraryWorkspaceButton("bulk", "Bulk import", "upload")}
            ${libraryWorkspaceButton("bulkJobs", "Bulk jobs", "clock")}
          </div>
        </div>
        <div class="library-layout">
          <div class="library-workspace">
            <section id="library-workspace-catalog" class="panel library-workspace-panel">
              <div class="panel-head">
                <h2>Catalog</h2>
                <span id="library-list-count" class="muted">0 shown</span>
              </div>
              <div class="list-tools">
                <input id="library-search" placeholder="Search library, source, status" />
                <select id="library-status-filter">
                  <option value="">All status</option>
                  <option value="published">Published</option>
                  <option value="indexing">Indexing</option>
                  <option value="failed">Failed</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
              <div id="libraries-list" class="list"></div>
            </section>
            <section id="library-workspace-add" class="panel library-workspace-panel hidden">
              <div class="panel-head">
                <h2>Add source</h2>
                <span class="muted">Create a library or attach the first managed source.</span>
              </div>
              <div class="form-grid compact-form">
                <label>Source type<select id="new-source-type">
                  <option value="github_repo">GitHub repo</option>
                  <option value="website">Website</option>
                  <option value="llms_txt">llms.txt</option>
                  <option value="openapi">OpenAPI</option>
                  <option value="uploaded_docs">Uploaded docs</option>
                </select></label>
                <label>URL<input id="new-url" placeholder="https://github.com/owner/repo" /></label>
                <label class="website-field hidden">Website base URL<input id="new-website-base-url" placeholder="https://docs.example.com/docs" /></label>
	                <label>Library ID<input id="new-library-id" placeholder="/owner/repo" /></label>
	                <label>Display name<input id="new-display-name" /></label>
	                <label>Description<textarea id="new-description" class="short-text"></textarea></label>
	                <div class="source-subgrid">
	                  <label>Source role<select id="new-source-role">
	                    <option value="">Auto</option>
	                    <option value="primary_docs">Primary docs</option>
	                    <option value="website_docs">Website docs</option>
	                    <option value="llms_manifest">LLMS manifest</option>
	                    <option value="api_reference">API reference</option>
	                    <option value="manual_docs">Manual docs</option>
	                  </select></label>
	                  <label>Priority<input id="new-aggregate-priority" type="number" min="1" placeholder="auto" /></label>
	                </div>
		                <label class="non-github-version">Version label<input id="new-version-label" placeholder="latest" /></label>
	                <label class="github-field">Default branch<input id="new-ref" placeholder="detect default branch" /></label>
	                <p class="github-field form-hint">Inspect a GitHub repo to review the default branch and select up to 19 release tags before indexing.</p>
                <label class="github-field">Include<textarea id="new-include" class="short-text" placeholder="docs/**&#10;README.md"></textarea></label>
                <label class="github-field">Exclude<textarea id="new-exclude" class="short-text" placeholder="src/**&#10;test/**"></textarea></label>
                <label class="website-field hidden">Max pages<input id="new-max-pages" type="number" min="1" placeholder="1000" /></label>
                <label class="website-field hidden check-row"><input id="new-respect-robots" type="checkbox" checked /> Respect robots.txt</label>
                <label>Rules<textarea id="new-rules" class="short-text"></textarea></label>
              </div>
              <div class="actions padded">
                <button id="inspect-source" type="button" data-icon="search">Inspect source</button>
                <button id="create-library" class="primary" type="button" data-icon="play">Create and index</button>
              </div>
              <div id="source-inspection-result" class="inspection hidden"></div>
              <pre id="library-create-result" class="code"></pre>
            </section>
            <section id="library-workspace-bulk" class="panel library-workspace-panel hidden">
              <div class="panel-head">
                <h2>Bulk import</h2>
                <span class="muted">Organize many URLs, review removals, then submit selected sources.</span>
              </div>
              <div class="bulk-panel">
                <label>URLs<textarea id="bulk-urls" class="bulk-text" placeholder="https://github.com/vercel/next.js&#10;https://nextjs.org/docs&#10;https://docs.example.com/openapi.yaml"></textarea></label>
                <label>CSV or JSON file<input id="bulk-file" type="file" accept=".csv,.json,.txt,text/csv,application/json,text/plain" /></label>
                <div class="bulk-controls">
                  <label>Max pages<input id="bulk-max-pages" type="number" min="1" value="250" /></label>
                  <label class="check-row"><input id="bulk-force" type="checkbox" /> Force refresh</label>
                </div>
                <div class="actions">
                  <button id="bulk-organize" type="button" data-icon="search">Organize</button>
                  <button id="bulk-submit" class="primary" type="button" data-icon="play" disabled>Submit selected</button>
                </div>
              </div>
              <div id="bulk-result" class="bulk-result"></div>
            </section>
            <section id="library-workspace-bulkJobs" class="panel library-workspace-panel hidden">
              <div class="panel-head">
                <h2>Bulk jobs</h2>
                <button id="reload-bulk-jobs" type="button" data-icon="refresh">Reload jobs</button>
              </div>
              <div id="bulk-jobs" class="bulk-jobs"></div>
            </section>
          </div>

          <section id="library-detail" class="panel hidden">
            <div class="panel-head">
              <h2 id="library-title">Library</h2>
              <div class="filters">
                <button id="refresh-library" type="button" data-icon="refresh">Refresh</button>
                <button id="force-refresh-library" type="button" data-icon="rotate">Force</button>
                <button id="delete-selected-library" class="danger" type="button" data-icon="trash">Delete</button>
              </div>
            </div>
            <div id="library-summary" class="summary-strip"></div>
            <div class="tabs">
              ${libraryTabButton("overview", "Overview")}
              ${libraryTabButton("configuration", "Configuration")}
              ${libraryTabButton("sources", "Sources")}
              ${libraryTabButton("aggregate", "Aggregate")}
              ${libraryTabButton("versions", "Versions")}
              ${libraryTabButton("indexing", "Indexing")}
              ${libraryTabButton("preview", "Preview")}
              ${libraryTabButton("quality", "Quality")}
              ${libraryTabButton("metrics", "Metrics")}
            </div>
            <div id="library-body" class="detail-body"></div>
          </section>
        </div>
      </section>

      <section id="view-claims" class="view">
        <div class="page-toolbar">
          <div>
            <h1>Claim Requests</h1>
            <p class="muted">Approve ownership claims and verified library badges.</p>
          </div>
          <button id="reload-claims" type="button" data-icon="refresh">Reload</button>
        </div>
        <section class="panel">
          <div id="claim-requests-list" class="list"></div>
        </section>
      </section>

      <section id="view-requests" class="view">
        <div class="page-toolbar">
          <div>
            <h1>Requested Libraries</h1>
            <p class="muted">Review library sources users could not find.</p>
          </div>
          <button id="reload-requests" type="button" data-icon="refresh">Reload</button>
        </div>
        <section class="panel">
          <div id="library-requests-list" class="list"></div>
        </section>
      </section>

      <section id="view-support" class="view">
        <div class="page-toolbar">
          <div>
            <h1>Support Issues</h1>
            <p class="muted">User-submitted bugs and product feedback.</p>
          </div>
          <button id="reload-support" type="button" data-icon="refresh">Reload</button>
        </div>
        <section class="panel">
          <div id="support-issues-list" class="list"></div>
        </section>
      </section>

      <section id="view-sources" class="view">
        <div class="source-layout">
          <section class="panel">
            <div class="panel-head">
              <h2>Managed sources</h2>
              <button id="reload-sources" type="button" data-icon="refresh">Reload</button>
            </div>
            <div id="sources-list" class="list"></div>
          </section>
          <details class="panel advanced-panel">
            <summary>Advanced JSON source submit</summary>
            <div class="panel-head">
              <h2>Raw source JSON</h2>
              <button id="format-source" type="button" data-icon="file">Format</button>
            </div>
            <textarea id="source-json" spellcheck="false"></textarea>
            <div class="actions padded">
              <button id="submit-source" class="primary" type="button" data-icon="play">Submit</button>
            </div>
            <pre id="source-result" class="code"></pre>
          </details>
        </div>
      </section>

      <section id="view-refreshTiers" class="view">
        <div class="page-toolbar">
          <div>
            <h1>Refresh Tiers</h1>
            <p class="muted">Control automatic recrawl priority for indexed sources.</p>
          </div>
          <div class="toolbar-actions">
            <input id="refresh-tier-search" class="toolbar-input" placeholder="Search library, source, URL" />
            <button id="reload-refresh-tiers" type="button" data-icon="refresh">Reload</button>
          </div>
        </div>
        <div id="refresh-tier-report"></div>
      </section>

      <section id="view-queue" class="view">
        <div class="page-toolbar">
          <div>
            <h1>Queue</h1>
            <p class="muted">Admission control before jobs enter NATS.</p>
          </div>
          <div class="toolbar-actions">
            <select id="admission-status-filter">
              <option value="">All status</option>
              <option value="queued">Queued</option>
              <option value="admitted">Admitted</option>
              <option value="running">Running</option>
              <option value="failed">Failed</option>
              <option value="canceled">Canceled</option>
              <option value="done">Done</option>
            </select>
            <select id="admission-intent-filter">
              <option value="">All lanes</option>
              <option value="new_ingest">New ingest</option>
              <option value="refresh">Refresh</option>
              <option value="migration">Migration</option>
            </select>
            <select id="admission-urgency-filter">
              <option value="">All urgency</option>
              <option value="priority">Priority</option>
              <option value="normal">Normal</option>
            </select>
            <button id="dispatch-admissions" type="button" data-icon="play">Dispatch</button>
            <button id="reload-admissions" type="button" data-icon="refresh">Reload</button>
          </div>
        </div>
        <div id="admission-counters" class="counter-grid compact"></div>
        <section class="panel">
          <div class="panel-head">
            <h2>Admissions</h2>
            <span id="admission-list-count" class="muted">0 shown</span>
          </div>
          <div id="admissions-list" class="list"></div>
        </section>
      </section>

      <section id="view-runs" class="view">
        <div id="run-counters" class="counter-grid compact"></div>
        <section class="panel">
          <div class="panel-head">
            <h2>Runs</h2>
            <div class="filters">
              <select id="run-status">
                <option value="">All</option>
                <option value="queued">Queued</option>
                <option value="running">Running</option>
                <option value="failed">Failed</option>
                <option value="published">Published</option>
                <option value="canceled">Canceled</option>
              </select>
              <select id="run-stage">
                <option value="">All stages</option>
                ${stages.map((stage) => `<option value="${stage}">${stage}</option>`).join("")}
              </select>
              <button id="cancel-selected-runs" type="button" data-icon="ban">Cancel selected</button>
              <button id="reload-runs" type="button" data-icon="refresh">Reload</button>
            </div>
          </div>
          <div id="runs-list" class="list"></div>
        </section>

        <section id="run-detail" class="panel hidden detail-panel">
          <div class="panel-head">
            <h2 id="run-title">Run</h2>
            <div class="filters">
              <button id="retry-run" type="button" data-icon="rotate">Retry</button>
              <button id="cancel-run" class="danger" type="button" data-icon="ban">Cancel</button>
            </div>
          </div>
          <div id="stage-timeline" class="timeline"></div>
          <div class="tabs">
            ${runTabButton("events", "Events")}
            ${runTabButton("diagnostics", "Diagnostics")}
            ${runTabButton("artifacts", "Artifacts")}
            ${runTabButton("crawl", "Crawl audit")}
            ${runTabButton("quarantine", "Quarantine")}
          </div>
          <div id="run-output" class="code tall"></div>
        </section>
      </section>

      <section id="view-usage" class="view">
        <div class="page-toolbar">
          <div>
            <h1>Usage</h1>
            <p class="muted">Users, SDKs, latest queries, misses, and limit hits.</p>
          </div>
          <div class="toolbar-actions usage-toolbar-actions">
            <input id="usage-search" placeholder="Search users or SDKs" />
            <button id="reload-usage" type="button" data-icon="refresh">Reload</button>
          </div>
        </div>
        <div id="usage-report"></div>
      </section>

      <section id="view-limits" class="view">
        <div class="page-toolbar">
          <div>
            <h1>Limits</h1>
            <p class="muted">Edit RPM, RPH, 5-hour, and monthly get-library-docs caps.</p>
          </div>
          <button id="reload-limits" type="button" data-icon="refresh">Reload</button>
        </div>
        <div id="limits-report"></div>
      </section>

      <section id="view-logs" class="view">
        <section class="panel">
          <div class="panel-head">
            <h2>Logs</h2>
            <div class="filters">
              <span id="log-stream-state" class="muted">idle</span>
              <button id="reload-logs" type="button" data-icon="refresh">Reload</button>
              <button id="stream-logs" type="button" data-icon="play">Live</button>
              <button id="pause-logs" type="button" data-icon="ban">Pause</button>
            </div>
          </div>
          <div class="log-filters">
            <div class="quick-filters">
              <button type="button" data-log-quick="errors">Errors</button>
              <button type="button" data-log-quick="publish">Publish gates</button>
              <button type="button" data-log-quick="discover">Discover</button>
              <button type="button" data-log-quick="clear">Clear</button>
            </div>
            <label>Run ID<input id="log-run-id" placeholder="run_..." /></label>
            <label>Source ID<input id="log-source-id" placeholder="src_..." /></label>
            <label>Library ID<input id="log-library-id" placeholder="/owner/repo" /></label>
            <label>Stage<select id="log-stage">
              <option value="">All</option>
              ${stages.map((stage) => `<option value="${stage}">${stage}</option>`).join("")}
            </select></label>
            <label>Level<select id="log-level">
              <option value="">All</option>
              <option value="info">Info</option>
              <option value="warn">Warn</option>
              <option value="error">Error</option>
            </select></label>
            <label>Event type<input id="log-event-type" placeholder="stage_failed" /></label>
            <label>Limit<select id="log-limit">
              <option value="100">100</option>
              <option value="500" selected>500</option>
              <option value="1000">1000</option>
              <option value="2000">2000</option>
            </select></label>
          </div>
          <div class="logs-layout">
            <div id="logs-list" class="log-list"></div>
            <section class="payload-panel">
              <div class="payload-head">
                <strong>Event payload</strong>
                <span id="log-count" class="muted">0 events</span>
              </div>
              <pre id="log-payload" class="code tall">Select an event.</pre>
            </section>
          </div>
        </section>
      </section>

      <section id="view-notifications" class="view">
        <section class="panel">
          <div class="panel-head">
            <h2>Notifications</h2>
            <div class="filters">
              <span id="notification-count" class="muted">0 unread</span>
              <button id="reload-notifications" type="button" data-icon="refresh">Reload</button>
              <button id="mark-all-notifications" type="button" data-icon="check">Mark all read</button>
            </div>
          </div>
          <div id="notifications-list" class="notification-list"></div>
        </section>
      </section>

      <section id="view-dead" class="view">
        <section class="panel">
          <div class="panel-head">
            <h2>Dead letters</h2>
            <button id="reload-dead" type="button" data-icon="refresh">Reload</button>
          </div>
          <div id="dead-list" class="list"></div>
        </section>
      </section>

      <section id="view-system" class="view">
        <section class="panel">
          <div class="panel-head">
            <h2>System</h2>
            <div class="filters">
              <button id="reload-system" type="button" data-icon="refresh">Reload</button>
              <button id="system-nats" type="button" data-icon="server">NATS</button>
              <button id="system-qdrant" type="button" data-icon="database">Qdrant</button>
              <button id="system-retrieval" type="button" data-icon="search">Retrieval</button>
              <button id="system-embeddings" type="button" data-icon="activity">Embeddings</button>
              <button id="system-config" type="button" data-icon="settings">Config</button>
            </div>
          </div>
          <div id="system-health" class="system-health"></div>
          <pre id="system-output" class="code tall"></pre>
        </section>
      </section>
    </main>
  </div>

  <div id="modal" class="modal hidden" role="dialog" aria-modal="true">
    <section class="modal-panel">
      <div class="modal-head">
        <h2 id="modal-title">Review</h2>
        <button id="modal-close" type="button">Close</button>
      </div>
      <div id="modal-body" class="modal-body"></div>
    </section>
  </div>
`;

if (!redirectToConfiguredLoopbackHost()) {
  bind();
  renderIcons();
  void bootstrap();
}

function bind() {
  byID("auth-form").addEventListener("submit", (event) => {
    event.preventDefault();
    void submitAuth();
  });
  byID("auth-switch").addEventListener("click", toggleAuthMode);
  byID("logout").addEventListener("click", () => void logout());
  document.querySelectorAll<HTMLButtonElement>("[data-view]").forEach((button) => {
    button.addEventListener("click", () => switchView(button.dataset.view as ViewName));
  });
  document.querySelectorAll<HTMLButtonElement>("[data-library-tab]").forEach((button) => {
    button.addEventListener("click", () => switchLibraryTab(button.dataset.libraryTab as LibraryTab));
  });
  document.querySelectorAll<HTMLButtonElement>("[data-library-workspace]").forEach((button) => {
    button.addEventListener("click", () => switchLibraryWorkspace(button.dataset.libraryWorkspace as LibraryWorkspace));
  });
  document.querySelectorAll<HTMLButtonElement>("[data-run-tab]").forEach((button) => {
    button.addEventListener("click", () => switchRunTab(button.dataset.runTab as RunTab));
  });
  byID("refresh").addEventListener("click", () => void refreshAll());
  byID("notification-sound").addEventListener("click", toggleNotificationSound);
  byID("reload-libraries").addEventListener("click", () => void loadLibraries());
  byID("reload-claims").addEventListener("click", () => void loadClaimRequests());
  byID("reload-requests").addEventListener("click", () => void loadLibraryRequests());
  byID("reload-support").addEventListener("click", () => void loadSupportIssues());
  byID<HTMLInputElement>("library-search").addEventListener("input", () => {
    state.librarySearch = byID<HTMLInputElement>("library-search").value;
    renderLibrariesList();
  });
  byID<HTMLSelectElement>("library-status-filter").addEventListener("change", () => {
    state.libraryStatusFilter = byID<HTMLSelectElement>("library-status-filter").value;
    renderLibrariesList();
  });
  byID("new-source-type").addEventListener("change", handleNewSourceTypeChange);
  byID("inspect-source").addEventListener("click", () => void inspectSource());
  byID("create-library").addEventListener("click", () => void createLibrary());
  byID("bulk-organize").addEventListener("click", () => void organizeBulkSources());
  byID("bulk-submit").addEventListener("click", () => void submitBulkSources());
  byID("reload-bulk-jobs").addEventListener("click", () => void loadBulkJobs());
  byID<HTMLInputElement>("bulk-file").addEventListener("change", () => void loadBulkFile());
  byID("refresh-library").addEventListener("click", () => void refreshSelectedLibrary(false));
  byID("force-refresh-library").addEventListener("click", () => void refreshSelectedLibrary(true));
  byID("delete-selected-library").addEventListener("click", () => void openSelectedLibraryDeleteModal());
  byID("reload-sources").addEventListener("click", () => void loadSources());
  byID("reload-refresh-tiers").addEventListener("click", () => void loadRefreshTiers());
  byID<HTMLInputElement>("refresh-tier-search").addEventListener("input", () => {
    state.refreshTierSearch = byID<HTMLInputElement>("refresh-tier-search").value;
    renderRefreshTiers();
  });
  byID("reload-admissions").addEventListener("click", () => void loadAdmissionsQueue());
  byID("dispatch-admissions").addEventListener("click", () => void dispatchAdmissions());
  byID<HTMLSelectElement>("admission-status-filter").addEventListener("change", () => {
    state.admissionStatusFilter = byID<HTMLSelectElement>("admission-status-filter").value;
    void loadAdmissionsQueue();
  });
  byID<HTMLSelectElement>("admission-intent-filter").addEventListener("change", () => {
    state.admissionIntentFilter = byID<HTMLSelectElement>("admission-intent-filter").value;
    void loadAdmissionsQueue();
  });
  byID<HTMLSelectElement>("admission-urgency-filter").addEventListener("change", () => {
    state.admissionUrgencyFilter = byID<HTMLSelectElement>("admission-urgency-filter").value;
    void loadAdmissionsQueue();
  });
  byID("reload-runs").addEventListener("click", () => void loadRuns());
  byID("reload-usage").addEventListener("click", () => void loadUsage());
  byID<HTMLInputElement>("usage-search").addEventListener("input", () => {
    state.usageSearch = byID<HTMLInputElement>("usage-search").value;
    state.usageUserPage = 1;
    state.usageLibraryPage = 1;
    renderUsage();
  });
  byID("reload-limits").addEventListener("click", () => void loadUsage());
  byID("reload-logs").addEventListener("click", () => void loadLogs());
  byID("stream-logs").addEventListener("click", () => void connectLogStream());
  byID("pause-logs").addEventListener("click", disconnectLogStream);
  byID("reload-notifications").addEventListener("click", () => void loadNotifications());
  byID("mark-all-notifications").addEventListener("click", () => void markAllNotificationsRead());
  byID("reload-dead").addEventListener("click", () => void loadDeadLetters());
  byID("reload-system").addEventListener("click", () => void loadSystem("all"));
  byID("system-nats").addEventListener("click", () => void loadSystem("nats"));
  byID("system-qdrant").addEventListener("click", () => void loadSystem("qdrant"));
  byID("system-retrieval").addEventListener("click", () => void loadSystem("retrieval"));
  byID("system-embeddings").addEventListener("click", () => void loadSystem("embeddings"));
  byID("system-config").addEventListener("click", () => void loadSystem("config"));
  document.querySelectorAll<HTMLButtonElement>("[data-log-quick]").forEach((button) => {
    button.addEventListener("click", () => void applyLogQuickFilter(button.dataset.logQuick || ""));
  });
  byID("format-source").addEventListener("click", formatSource);
  byID("submit-source").addEventListener("click", () => void submitSource());
  byID("run-status").addEventListener("change", () => void loadRuns());
  byID("run-stage").addEventListener("change", () => void loadRuns());
  byID("cancel-selected-runs").addEventListener("click", () => void cancelSelectedRuns());
  byID("retry-run").addEventListener("click", () => void retryRun());
  byID("cancel-run").addEventListener("click", () => void cancelRun());
  byID("modal-close").addEventListener("click", closeModal);
  byID<HTMLTextAreaElement>("source-json").value = JSON.stringify(sampleSource, null, 2);
  handleNewSourceTypeChange();
  switchLibraryWorkspace(state.libraryWorkspace);
  switchView(state.view, { syncRoute: false, load: false });
  window.addEventListener("popstate", () => {
    switchView(viewFromPathname(window.location.pathname), { syncRoute: false });
  });
}

async function bootstrap() {
  try {
    const response = await api.me();
    state.session = response.user;
    showAuthenticated();
    await refreshAll();
    startLiveRefresh();
    connectNotificationStream();
  } catch {
    showAuth("");
  }
}

async function submitAuth() {
  state.baseURL = state.baseURL || defaultAPIBaseURL();
  api.setBaseURL(state.baseURL);
  const email = byID<HTMLInputElement>("auth-email").value.trim();
  const password = byID<HTMLInputElement>("auth-password").value;
  const displayName = byID<HTMLInputElement>("auth-display-name").value.trim();
  setAuthMessage("Checking credentials", false);
  try {
    const response =
      state.authMode === "register" ? await api.register(email, password, displayName) : await api.login(email, password);
    state.session = response.user;
    showAuthenticated();
    await refreshAll();
    startLiveRefresh();
    connectNotificationStream();
  } catch (error) {
    setAuthMessage(error instanceof Error ? error.message : String(error), true);
  }
}

async function logout() {
  stopLiveRefresh();
  disconnectLogStream();
  disconnectNotificationStream();
  state.eventStream?.close();
  state.eventStream = null;
  try {
    await api.logout();
  } finally {
    state.session = null;
    showAuth("");
  }
}

function toggleAuthMode() {
  state.authMode = state.authMode === "login" ? "register" : "login";
  renderAuthMode();
}

function renderAuthMode() {
  const registering = state.authMode === "register";
  byID("auth-title").textContent = registering ? "Create admin account" : "Admin sign in";
  byID("auth-display-row").classList.toggle("hidden", !registering);
  byID("auth-submit").textContent = registering ? "Create account" : "Sign in";
  byID("auth-switch").textContent = registering ? "Use existing account" : "Create account";
  renderIcons();
}

function showAuthenticated() {
  byID("auth-screen").classList.add("hidden");
  byID("shell").classList.remove("hidden");
  byID("account-email").textContent = state.session?.email || "";
  switchView(state.view, { syncRoute: false, load: false });
  startLiveRefresh();
}

function showAuth(message: string) {
  stopLiveRefresh();
  byID("shell").classList.add("hidden");
  byID("auth-screen").classList.remove("hidden");
  renderAuthMode();
  setAuthMessage(message, Boolean(message));
}

function setAuthMessage(message: string, isError: boolean) {
  const element = byID("auth-message");
  element.textContent = message;
  element.classList.toggle("error", isError);
}

function startLiveRefresh() {
  if (liveRefreshTimer !== null) return;
  liveRefreshTimer = window.setInterval(() => void refreshLiveData(), liveRefreshIntervalMs);
}

function stopLiveRefresh() {
  if (liveRefreshTimer === null) return;
  window.clearInterval(liveRefreshTimer);
  liveRefreshTimer = null;
  liveRefreshInFlight = false;
}

function formControlFocused() {
  const tagName = document.activeElement?.tagName;
  return tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT";
}

async function refreshLiveData() {
  if (!state.session || liveRefreshInFlight) return;
  liveRefreshInFlight = true;
  try {
    await Promise.all([loadOverview(), loadRuns()]);
    if (state.view === "libraries") {
      if (state.libraryWorkspace === "catalog" && !formControlFocused()) {
        await loadLibraries();
        if (state.selectedLibrary) await selectLibrary(state.selectedLibrary);
      } else if (state.libraryWorkspace === "bulkJobs") {
        await loadBulkJobs();
      }
    }
    if (state.view === "dead") await loadDeadLetters();
    if (state.view === "notifications") await loadNotifications();
    if (state.view === "queue") await loadAdmissionsQueue();
  } catch (error) {
    if (isUnauthorized(error)) {
      state.session = null;
      showAuth("Session expired. Sign in again.");
      return;
    }
    console.warn("live refresh failed", error);
  } finally {
    liveRefreshInFlight = false;
  }
}

async function refreshAll() {
  if (!state.session) return;
  try {
    await loadOverview();
    await loadVisibleViewData(state.view);
  } catch (error) {
    if (isUnauthorized(error)) {
      state.session = null;
      showAuth("Session expired. Sign in again.");
      return;
    }
    showCode("system-output", error);
  }
}

async function loadVisibleViewData(view: ViewName) {
  if (view === "libraries") {
    await loadLibraries();
    if (state.libraryWorkspace === "bulkJobs") await loadBulkJobs();
    if (state.selectedLibrary) await selectLibrary(state.selectedLibrary);
    return;
  }
  if (view === "claims") {
    await loadClaimRequests();
    return;
  }
  if (view === "requests") {
    await loadLibraryRequests();
    return;
  }
  if (view === "support") {
    await loadSupportIssues();
    return;
  }
  if (view === "sources") {
    await loadSources();
    return;
  }
  if (view === "refreshTiers") {
    await loadRefreshTiers();
    return;
  }
  if (view === "queue") {
    await loadAdmissionsQueue();
    return;
  }
  if (view === "runs") {
    await loadRuns();
    return;
  }
  if (view === "usage" || view === "limits") {
    await loadUsage();
    return;
  }
  if (view === "logs") {
    await loadLogs();
    return;
  }
  if (view === "notifications") {
    await loadNotifications();
    return;
  }
  if (view === "dead") {
    await loadDeadLetters();
    return;
  }
  if (view === "system") {
    await loadSystem("all");
  }
}

async function loadOverview() {
  state.overview = await api.overview();
  renderOverviewCounters();
  renderRunCounters();
}

async function loadLibraries() {
  state.libraries = await api.libraries();
  renderLibrariesList();
  if (!state.selectedLibrary && state.libraries.length) await selectLibrary(state.libraries[0].libraryId);
}

async function loadClaimRequests() {
  state.claimRequests = await api.userRequests("library_claim");
  renderUserRequests("claim-requests-list", state.claimRequests, "claims");
}

async function loadLibraryRequests() {
  state.libraryRequests = await api.userRequests("library_index");
  renderUserRequests("library-requests-list", state.libraryRequests, "requests");
}

async function loadSupportIssues() {
  state.supportIssues = await api.userRequests("support_issue");
  renderUserRequests("support-issues-list", state.supportIssues, "support");
}

async function loadUsage() {
  state.usage = await api.usage();
  renderUsage();
  void loadUsageMisses();
  renderLimits();
  if (state.usageDetail) void loadUsageEvents();
}

async function loadUsageEvents() {
  if (!state.usageDetail || state.usageDetail.scopeType === "miss") return;
  state.usageEventsLoading = true;
  renderUsage();
  try {
    state.usageEvents = await api.usageEvents(state.usageDetail.scopeType, state.usageDetail.scopeId, "", 50);
  } catch {
    state.usageEvents = { generatedAt: "", events: [] };
  } finally {
    state.usageEventsLoading = false;
    renderUsage();
  }
}

async function loadUsageMisses() {
  state.usageMissesLoading = true;
  renderUsage();
  try {
    state.usageMisses = await api.usageMisses(state.usageSearch, 200);
  } catch {
    state.usageMisses = { generatedAt: "", misses: [] };
  } finally {
    state.usageMissesLoading = false;
    renderUsage();
  }
}

function renderUsage() {
  const node = byID("usage-report");
  if (!state.usage) {
    node.innerHTML = empty("No usage data loaded.");
    return;
  }
  node.innerHTML = renderUsageReport(state.usage, state.usageDetail, state.usageSearch, {
    userPage: state.usageUserPage,
    libraryPage: state.usageLibraryPage,
    pageSize: state.usagePageSize
  }, state.usageMode, state.usageEvents, state.usageEventsLoading, state.usageMisses, state.usageMissesLoading);
  const searchInput = byID<HTMLInputElement>("usage-search");
  searchInput.placeholder = state.usageMode === "sdk" ? "Search missing library, query, user, client" : "Search user or email";
  node.querySelectorAll<HTMLButtonElement>("[data-usage-detail]").forEach((button) => {
    button.addEventListener("click", () => {
      const scopeType = button.dataset.usageDetail;
      if (scopeType !== "user" && scopeType !== "key" && scopeType !== "library" && scopeType !== "miss") return;
      state.usageDetail = { scopeType, scopeId: button.dataset.usageScopeId || "" };
      state.usageEvents = null;
      renderUsage();
      if (scopeType !== "miss") void loadUsageEvents();
    });
  });
  node.querySelectorAll<HTMLButtonElement>("[data-usage-mode]").forEach((button) => {
    button.addEventListener("click", () => {
      const mode = button.dataset.usageMode;
      if (mode !== "activeUsers" && mode !== "allUsers" && mode !== "sdk") return;
      state.usageMode = mode;
      state.usageDetail = null;
      state.usageEvents = null;
      state.usageUserPage = 1;
      state.usageLibraryPage = 1;
      renderUsage();
    });
  });
  node.querySelectorAll<HTMLButtonElement>("[data-usage-page]").forEach((button) => {
    button.addEventListener("click", () => {
      const targetPage = Math.max(1, Number(button.dataset.usagePageTarget || 1));
      if (button.dataset.usagePage === "user") state.usageUserPage = targetPage;
      if (button.dataset.usagePage === "library") state.usageLibraryPage = targetPage;
      renderUsage();
    });
  });
  node.querySelectorAll<HTMLButtonElement>("[data-save-usage-policy]").forEach((button) => {
    button.addEventListener("click", () => void saveUsagePolicy(button));
  });
  renderIcons();
}

function renderLimits() {
  const node = byID("limits-report");
  if (!state.usage) {
    node.innerHTML = empty("No limits loaded.");
    return;
  }
  node.innerHTML = renderUsageLimits(state.usage);
  node.querySelectorAll<HTMLButtonElement>("[data-save-usage-policy]").forEach((button) => {
    button.addEventListener("click", () => void saveUsagePolicy(button));
  });
  renderIcons();
}

async function saveUsagePolicy(button: HTMLButtonElement) {
  const row = button.closest<HTMLElement>("[data-usage-policy]");
  if (!row) return;
  const policy = usagePolicyFromNode(row);
  if (!policy) {
    openModal("Invalid limit", "<p>Enter a positive request limit before saving.</p>");
    return;
  }
  button.disabled = true;
  try {
    await api.updateUsagePolicy(policy);
    await loadUsage();
  } catch (error) {
    button.disabled = false;
    openModal("Usage policy error", `<pre>${escapeHTML(error instanceof Error ? error.message : String(error))}</pre>`);
  }
}

function renderLibrariesList() {
  const list = byID("libraries-list");
  const query = state.librarySearch.trim().toLowerCase();
  const status = state.libraryStatusFilter;
  const libraries = state.libraries.filter((profile) => {
    if (status && profile.status !== status) return false;
    if (!query) return true;
    return [
      profile.libraryId,
      profile.displayName,
      profile.description || "",
      profile.status,
      profile.defaultVersion || "",
      ...(profile.sourceIds || [])
    ].join(" ").toLowerCase().includes(query);
  });
  byID("library-list-count").textContent = `${formatCount(libraries.length)} shown`;
  const published = state.libraries.filter((profile) => profile.status === "published").length;
  const indexing = state.libraries.filter((profile) => profile.status === "indexing").length;
  const failed = state.libraries.filter((profile) => profile.status === "failed").length;
  byID("library-count-summary").textContent =
    `${formatCount(state.libraries.length)} total · ${formatCount(published)} published · ${formatCount(indexing)} indexing · ${formatCount(failed)} failed`;
  list.innerHTML = libraries.length ? "" : empty(state.libraries.length ? "No libraries match the filters." : "No libraries.");
  for (const profile of libraries) list.appendChild(libraryItem(profile));
  renderIcons();
}

function renderUserRequests(listID: string, requests: UserRequest[], view: "claims" | "requests" | "support") {
  const list = byID(listID);
  list.innerHTML = requests.length ? "" : empty("No requests.");
  for (const request of requests) list.appendChild(userRequestItem(request, view));
  renderIcons();
}

function userRequestItem(request: UserRequest, view: "claims" | "requests" | "support") {
  const item = document.createElement("article");
  item.className = "list-item request-item";
  const title = request.libraryName || request.title || request.libraryId || request.sourceUrl || request.requestId;
  const subject = request.libraryId || request.sourceUrl || request.currentUrl || request.userEmail || "-";
  const detail = request.message || request.claimRole || request.scope || "";
  const duplicate = request.duplicateCount && request.duplicateCount > 1 ? ` · ${request.duplicateCount} similar` : "";
  const requester = request.userEmail
    ? request.userName
      ? `${request.userName} <${request.userEmail}>`
      : request.userEmail
    : "anonymous";
  item.innerHTML = `
    <div>
      <strong>${escapeHTML(title)}</strong>
      <p>${escapeHTML(subject)}${escapeHTML(duplicate)}</p>
      <p>${escapeHTML(detail)}</p>
      <p>${escapeHTML(requester)} · ${escapeHTML(request.createdAt || "-")}</p>
    </div>
    <div class="request-actions">
      ${statusBadge(request.status)}
      ${request.sourceUrl ? `<a href="${escapeHTML(request.sourceUrl)}" target="_blank" rel="noreferrer">Source</a>` : ""}
      ${request.currentUrl ? `<a href="${escapeHTML(request.currentUrl)}" target="_blank" rel="noreferrer">Page</a>` : ""}
      ${requestActions(request, view)}
    </div>
  `;
  item.querySelectorAll<HTMLButtonElement>("[data-request-status]").forEach((button) => {
    button.addEventListener("click", () => void updateUserRequestStatus(request, button.dataset.requestStatus || "open"));
  });
  return item;
}

function requestActions(request: UserRequest, view: "claims" | "requests" | "support") {
  if (view === "support") {
    return request.status === "closed"
      ? `<button type="button" data-request-status="open" data-icon="rotate">Reopen</button>`
      : `<button type="button" data-request-status="closed" data-icon="check">Close</button>`;
  }
  if (request.status !== "open") return "";
  return `
    <button type="button" class="primary" data-request-status="approved" data-icon="check">Approve</button>
    <button type="button" data-request-status="rejected" data-icon="ban">Reject</button>
  `;
}

async function updateUserRequestStatus(request: UserRequest, status: string) {
  await api.updateUserRequest(request.requestId, { status: status as UserRequest["status"] });
  if (request.kind === "library_claim") {
    await Promise.all([loadClaimRequests(), loadLibraries()]);
  } else if (request.kind === "library_index") {
    await loadLibraryRequests();
  } else {
    await loadSupportIssues();
  }
}

function renderOverviewCounters() {
  const overview = state.overview;
  const strip = byID("admin-overview-strip");
  if (!overview) {
    strip.innerHTML = "";
    return;
  }
  const queued = statusCount(overview.runsByStatus, "queued");
  const running = statusCount(overview.runsByStatus, "running");
  const failed = statusCount(overview.runsByStatus, "failed");
  const activeBulk = statusCount(overview.bulkJobsByStatus, "queued") + statusCount(overview.bulkJobsByStatus, "running");
  strip.innerHTML = `
    ${counterCard("Libraries", overview.librariesTotal, `${overview.publishedLibraries} published`, "libraries", "", "catalog")}
    ${counterCard("Queued runs", queued, "waiting for workers", "runs", "queued")}
    ${counterCard("Running runs", running, "currently indexing", "runs", "running")}
    ${counterCard("Failed runs", failed, "need review", "runs", "failed")}
    ${counterCard("Live published", overview.publishedLibraries, "queryable libraries", "libraries", "", "catalog")}
    ${counterCard("Bulk jobs", overview.bulkJobsTotal, `${activeBulk} active`, "libraries", "", "bulkJobs")}
    ${counterCard("Dead letters", overview.deadLetters, "terminal jobs", "dead", "")}
  `;
  strip.querySelectorAll<HTMLButtonElement>("[data-counter-view]").forEach((button) => {
    button.addEventListener("click", () => {
      const view = button.dataset.counterView as ViewName;
      const status = button.dataset.counterStatus || "";
      switchView(view);
      if (view === "libraries" && button.dataset.counterWorkspace) {
        switchLibraryWorkspace(button.dataset.counterWorkspace as LibraryWorkspace);
      }
      if (view === "runs") {
        byID<HTMLSelectElement>("run-status").value = status;
        byID<HTMLSelectElement>("run-stage").value = button.dataset.counterStage || "";
        void loadRuns();
      }
    });
  });
  renderIcons();
}

function renderRunCounters() {
  const overview = state.overview;
  const node = byID("run-counters");
  if (!overview) {
    node.innerHTML = "";
    return;
  }
  node.innerHTML = `
    ${counterCard("All runs", overview.runsTotal, "stored runs", "runs", "")}
    ${counterCard("Queued", statusCount(overview.runsByStatus, "queued"), "awaiting worker", "runs", "queued")}
    ${counterCard("Running", statusCount(overview.runsByStatus, "running"), "in progress", "runs", "running")}
    ${counterCard("Failed", statusCount(overview.runsByStatus, "failed"), "requires attention", "runs", "failed")}
    ${counterCard("Published", statusCount(overview.runsByStatus, "published"), "available for retrieval", "runs", "published")}
    ${counterCard("Discover", statusCount(overview.runsByStage, "discover"), "current stage", "runs", "", "", "discover")}
    ${counterCard("Embed", statusCount(overview.runsByStage, "embed"), "current stage", "runs", "", "", "embed")}
  `;
  node.querySelectorAll<HTMLButtonElement>("[data-counter-view]").forEach((button) => {
    button.addEventListener("click", () => {
      byID<HTMLSelectElement>("run-status").value = button.dataset.counterStatus || "";
      byID<HTMLSelectElement>("run-stage").value = button.dataset.counterStage || "";
      void loadRuns();
    });
  });
  renderIcons();
}

function counterCard(label: string, value: number, hint: string, view: ViewName, status: string, workspace = "", stage = "") {
  return `
    <button type="button" class="counter-card" data-counter-view="${view}" data-counter-status="${escapeHTML(status)}" data-counter-workspace="${escapeHTML(workspace)}" data-counter-stage="${escapeHTML(stage)}">
      <span>${escapeHTML(label)}</span>
      <strong>${formatCount(value)}</strong>
      <em>${escapeHTML(hint)}</em>
    </button>
  `;
}

function statusCount(values: Record<string, number> | undefined, key: string) {
  return values?.[key] || 0;
}

function formatCount(value: number) {
  return new Intl.NumberFormat().format(value || 0);
}

function unitCount(value: { unitCount?: number; snippetCount?: number }) {
  return value.unitCount ?? value.snippetCount ?? 0;
}

function unitCountLabel(value: { unitCount?: number; snippetCount?: number }) {
  const count = unitCount(value);
  return `${formatCount(count)} unit${count === 1 ? "" : "s"}`;
}

function handleNewSourceTypeChange() {
  const type = byID<HTMLSelectElement>("new-source-type").value as SourceType;
  const isGitHub = type === "github_repo";
  const isWebsite = type === "website";
  state.sourceInspection = null;
  byID("source-inspection-result").classList.add("hidden");
  byID("source-inspection-result").innerHTML = "";
  document.querySelectorAll<HTMLElement>(".github-field").forEach((node) => node.classList.toggle("hidden", !isGitHub));
  document.querySelectorAll<HTMLElement>(".website-field").forEach((node) => node.classList.toggle("hidden", !isWebsite));
  document.querySelectorAll<HTMLElement>(".non-github-version").forEach((node) => node.classList.toggle("hidden", isGitHub));
  const url = byID<HTMLInputElement>("new-url");
  const label = byID<HTMLInputElement>("new-version-label");
  label.placeholder = "latest";
  if (type === "github_repo") url.placeholder = "https://github.com/owner/repo";
  if (type === "website") url.placeholder = "https://docs.example.com";
  if (type === "llms_txt") url.placeholder = "https://docs.example.com/llms.txt";
  if (type === "openapi") url.placeholder = "https://api.example.com/openapi.yaml";
  if (type === "uploaded_docs") url.placeholder = "file upload source or signed URL";
  const role = byID<HTMLSelectElement>("new-source-role");
  if (!role.value) {
    role.value = defaultRoleForSourceType(type);
  }
}

function defaultRoleForSourceType(type: SourceType) {
  if (type === "github_repo") return "primary_docs";
  if (type === "website") return "website_docs";
  if (type === "llms_txt") return "llms_manifest";
  if (type === "openapi") return "api_reference";
  if (type === "uploaded_docs") return "manual_docs";
  return "";
}

function newLibraryRequest(includeVersionPlan = true): CreateLibraryRequest {
  const sourceType = byID<HTMLSelectElement>("new-source-type").value as SourceType;
  const isGitHub = sourceType === "github_repo";
  const isWebsite = sourceType === "website";
  const selectedVersions = includeVersionPlan && isGitHub ? selectedSourceVersions() : undefined;
  const selectedDefault = selectedVersions?.find((version) => version.isDefault);
  const refType = isGitHub ? "branch" : sourceRefType(sourceType);
  const ref = isGitHub
    ? selectedDefault?.ref || byID<HTMLInputElement>("new-ref").value.trim()
    : isWebsite
      ? byID<HTMLInputElement>("new-website-base-url").value.trim() || byID<HTMLInputElement>("new-url").value.trim()
      : byID<HTMLInputElement>("new-url").value.trim();
  const request: CreateLibraryRequest = {
    sourceType,
    sourceRole: byID<HTMLSelectElement>("new-source-role").value || undefined,
    aggregatePriority: numberValue("new-aggregate-priority"),
    url: byID<HTMLInputElement>("new-url").value.trim(),
    websiteBaseUrl: isWebsite ? byID<HTMLInputElement>("new-website-base-url").value.trim() : undefined,
    libraryId: byID<HTMLInputElement>("new-library-id").value.trim(),
    displayName: byID<HTMLInputElement>("new-display-name").value.trim(),
    description: byID<HTMLTextAreaElement>("new-description").value.trim(),
    versionLabel: isGitHub ? undefined : byID<HTMLInputElement>("new-version-label").value.trim() || "latest",
    refType,
    ref,
    crawlScope: {
      include: isGitHub ? linesFrom("new-include") : undefined,
      exclude: isGitHub ? linesFrom("new-exclude") : undefined,
      maxPages: isWebsite ? numberValue("new-max-pages") : undefined,
      respectRobots: isWebsite ? byID<HTMLInputElement>("new-respect-robots").checked : undefined
    },
    versionPolicy: {
      default_branch: isGitHub ? ref || undefined : undefined,
      tags: isGitHub ? selectedVersionRefs(selectedVersions, "tag") : undefined,
      branches: isGitHub ? selectedVersionRefs(selectedVersions, "branch", true) : undefined,
      semver: isGitHub
    },
    refreshPolicy: {
      mode: "webhook_plus_periodic",
      priority: "normal"
    },
    versions: selectedVersions,
    rules: linesFrom("new-rules")
  };
  return request;
}

function sourceRefType(sourceType: SourceType): "branch" | "tag" | "url" | "upload" | "manual" {
  if (sourceType === "website" || sourceType === "llms_txt" || sourceType === "openapi") return "url";
  if (sourceType === "uploaded_docs") return "upload";
  return "manual";
}

function selectedSourceVersions(): Partial<LibraryVersion>[] | undefined {
  const rows = Array.from(document.querySelectorAll<HTMLElement>("[data-source-version-row]"));
  if (!rows.length) return undefined;
  const versions: Partial<LibraryVersion>[] = [];
  for (const row of rows) {
    const checkbox = row.querySelector<HTMLInputElement>("[data-source-version-selected]");
    if (!checkbox?.checked) continue;
    const label = row.querySelector<HTMLInputElement>("[data-source-version-label]")?.value.trim() || "";
    const refType = row.querySelector<HTMLSelectElement>("[data-source-version-ref-type]")?.value as LibraryVersion["refType"];
    const ref = row.querySelector<HTMLInputElement>("[data-source-version-ref]")?.value.trim() || "";
    if (!label || !ref || !refType) continue;
    versions.push({
      label,
      title: label,
      refType,
      type: refType,
      ref,
      isDefault: row.dataset.defaultVersion === "true",
      source: "admin_create"
    });
    if (versions.length >= 20) break;
  }
  return versions.length ? versions : undefined;
}

function selectedVersionRefs(versions: Partial<LibraryVersion>[] | undefined, refType: "branch" | "tag", excludeDefault = false) {
  return (versions || [])
    .filter((version) => version.refType === refType && (!excludeDefault || !version.isDefault))
    .map((version) => version.ref || "")
    .filter(Boolean);
}

async function inspectSource() {
  try {
    const inspection = await api.inspectSource(newLibraryRequest(false));
    state.sourceInspection = inspection;
    applySourceInspection(inspection);
    renderSourceInspection(inspection);
  } catch (error) {
    state.sourceInspection = null;
    byID("source-inspection-result").classList.remove("hidden");
    byID("source-inspection-result").innerHTML = `<div class="inspection-row">${statusBadge("error")}<span>${escapeHTML(error instanceof Error ? error.message : String(error))}</span></div>`;
  }
  renderIcons();
}

function applySourceInspection(inspection: SourceInspection) {
  if (inspection.libraryId) byID<HTMLInputElement>("new-library-id").value = inspection.libraryId;
  if (inspection.displayName && !byID<HTMLInputElement>("new-display-name").value.trim()) {
    byID<HTMLInputElement>("new-display-name").value = inspection.displayName;
  }
  if (inspection.description && !byID<HTMLTextAreaElement>("new-description").value.trim()) {
    byID<HTMLTextAreaElement>("new-description").value = inspection.description;
  }
  if (inspection.versionLabel && inspection.sourceType !== "github_repo") byID<HTMLInputElement>("new-version-label").value = inspection.versionLabel;
  if (inspection.sourceType === "github_repo") {
    byID<HTMLInputElement>("new-ref").value = inspection.ref || "";
    byID<HTMLTextAreaElement>("new-include").value = (inspection.recommendedInclude || []).join("\n");
    byID<HTMLTextAreaElement>("new-exclude").value = (inspection.recommendedExclude || []).join("\n");
  }
  if (inspection.sourceType === "website" && inspection.websiteBaseUrl) {
    byID<HTMLInputElement>("new-website-base-url").value = inspection.websiteBaseUrl;
  }
}

function renderSourceInspection(inspection: SourceInspection) {
  const element = byID("source-inspection-result");
  element.classList.remove("hidden");
  const isGitHub = inspection.sourceType === "github_repo";
  element.innerHTML = `
    <div class="inspection-row">
      ${statusBadge(inspection.valid ? "succeeded" : "failed")}
      <span>${escapeHTML(inspection.message || "inspection complete")}</span>
    </div>
    <div class="inspection-grid">
      ${fieldView("Library ID", inspection.libraryId || "-")}
      ${fieldView(isGitHub ? "Default version" : "Version label", inspection.versionLabel || "-")}
      ${fieldView(isGitHub ? "Default ref type" : "Ref type", inspection.refType || "-")}
      ${fieldView(isGitHub ? "Default branch" : "Ref", inspection.ref || "-")}
      ${fieldView("Resolved SHA", inspection.resolvedSha || "-")}
      ${fieldView("Capabilities", (inspection.capabilities || []).join(", ") || "-")}
    </div>
    ${renderSourceVersionPlan(inspection)}
    ${(inspection.warnings || []).map((warning) => `<p class="warning-line">${escapeHTML(warning)}</p>`).join("")}
  `;
}

function renderSourceVersionPlan(inspection: SourceInspection) {
  if (inspection.sourceType !== "github_repo") return "";
  const discovery = inspection.versionDiscovery;
  const fallback: LibraryVersionCandidate = {
    label: "latest",
    type: "branch",
    refType: "branch",
    ref: inspection.ref || "",
    resolvedSha: inspection.resolvedSha,
    recommended: true,
    reason: "default branch"
  };
  const candidates = uniqueVersionCandidates([fallback, ...(discovery?.recommended || [])]).slice(0, 20);
  const rows = candidates.map((candidate, index) => sourceVersionPlanRow(candidate, index === 0)).join("");
  const branch = discovery?.defaultBranch || inspection.ref || "-";
  return `
    <div class="source-version-plan">
      <div class="source-version-head">
        <strong>Version plan</strong>
        <span>Default branch: ${escapeHTML(branch)} · ${candidates.length} selected max</span>
      </div>
      <div class="source-version-list">${rows}</div>
    </div>
  `;
}

function uniqueVersionCandidates(candidates: LibraryVersionCandidate[]) {
  const seen = new Set<string>();
  const out: LibraryVersionCandidate[] = [];
  for (const candidate of candidates) {
    const refType = candidate.refType || candidate.type;
    const ref = candidate.ref || candidate.label;
    const label = candidate.label || ref;
    if (!refType || !ref || !label) continue;
    const key = `${refType}:${ref}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...candidate, label, ref, refType, type: refType });
  }
  return out;
}

function sourceVersionPlanRow(candidate: LibraryVersionCandidate, isDefault: boolean) {
  const refType = candidate.refType || candidate.type;
  const ref = candidate.ref || candidate.label;
  const label = isDefault ? "latest" : candidate.label;
  const locked = isDefault ? "disabled" : "";
  return `
    <div class="source-version-row" data-source-version-row data-default-version="${isDefault ? "true" : "false"}">
      <input type="checkbox" data-source-version-selected ${candidate.recommended || isDefault ? "checked" : ""} ${locked} />
      <input data-source-version-label value="${escapeHTML(label)}" ${isDefault ? "readonly" : ""} />
      <select data-source-version-ref-type ${isDefault ? "disabled" : ""}>
        <option value="branch" ${refType === "branch" ? "selected" : ""}>branch</option>
        <option value="tag" ${refType === "tag" ? "selected" : ""}>tag</option>
      </select>
      <input data-source-version-ref value="${escapeHTML(ref)}" />
      <span>${escapeHTML(candidate.reason || "")}</span>
    </div>
  `;
}

async function createLibrary() {
  const request = newLibraryRequest();
  try {
    const response = await api.createLibrary(request);
    showCode("library-create-result", response);
    await Promise.all([loadOverview(), loadLibraries(), loadRuns()]);
    await selectLibrary(response.libraryId);
  } catch (error) {
    showCode("library-create-result", error);
  }
}

async function organizeBulkSources() {
  const text = byID<HTMLTextAreaElement>("bulk-urls").value.trim();
  const output = byID("bulk-result");
  if (!text) {
    output.innerHTML = `<p class="empty">Paste one or more URLs first.</p>`;
    return;
  }
  output.innerHTML = `<p class="empty">Organizing URLs...</p>`;
  byID<HTMLButtonElement>("bulk-submit").disabled = true;
  try {
    state.bulkResult = await api.bulkOrganize({
      text,
      defaultTenantId: "public",
      defaultVisibility: "public",
      maxPages: numberValue("bulk-max-pages") || 250,
      dryRun: true
    });
    renderBulkResult(state.bulkResult);
  } catch (error) {
    state.bulkResult = null;
    showCodeElement(output, error);
  }
  renderIcons();
}

async function loadBulkFile() {
  const input = byID<HTMLInputElement>("bulk-file");
  const file = input.files?.[0];
  if (!file) return;
  const text = await file.text();
  byID<HTMLTextAreaElement>("bulk-urls").value = text;
}

function renderBulkResult(result: BulkOrganizeResponse) {
  const output = byID("bulk-result");
  const acceptedRows = result.accepted
    .map((candidate) => bulkCandidateRow(candidate))
    .join("");
  const removedRows = result.removed
    .map(
      (row) => `
        <tr>
          <td>${escapeHTML(row.reason)}</td>
          <td>${escapeHTML(row.url || row.originalUrl)}</td>
          <td>${escapeHTML(row.message || "")}</td>
        </tr>
      `
    )
    .join("");
  output.innerHTML = `
    <div class="bulk-summary">
      ${fieldView("Accepted", String(result.accepted.length))}
      ${fieldView("Removed", String(result.removed.length))}
      ${fieldView("Organized", result.organizedAt)}
    </div>
    ${(result.warnings || []).map((warning) => `<p class="warning-line">${escapeHTML(warning)}</p>`).join("")}
    <div class="bulk-table-wrap">
      <table class="bulk-table">
        <thead><tr><th></th><th>Type</th><th>Library</th><th>URL</th><th>Versions</th><th>Role</th></tr></thead>
        <tbody>${acceptedRows || `<tr><td colspan="6">No accepted sources.</td></tr>`}</tbody>
      </table>
    </div>
    <div class="section-title">Removed URLs</div>
    <div class="bulk-table-wrap">
      <table class="bulk-table">
        <thead><tr><th>Reason</th><th>URL</th><th>Message</th></tr></thead>
        <tbody>${removedRows || `<tr><td colspan="3">No URLs removed.</td></tr>`}</tbody>
      </table>
    </div>
  `;
  byID<HTMLButtonElement>("bulk-submit").disabled = result.accepted.length === 0;
}

function bulkCandidateRow(candidate: BulkSourceCandidate) {
  const versions = candidate.versions || [];
  const versionBlock = candidate.sourceType === "github_repo"
    ? `
      <div class="bulk-version-block">
        <div class="bulk-version-meta">${escapeHTML(candidate.versionDiscovery?.defaultBranch || candidate.ref || "default branch pending")}</div>
        ${versions.length ? versions.map((version) => bulkVersionOption(version)).join("") : `<span class="muted">No versions discovered.</span>`}
        ${(candidate.warnings || []).map((warning) => `<div class="warning-text compact-warning">${escapeHTML(warning)}</div>`).join("")}
      </div>
    `
    : `<span class="muted">${escapeHTML(candidate.versionLabel || candidate.ref || "latest")}</span>`;
  return `
    <tr data-bulk-candidate="${escapeHTML(candidate.id)}">
      <td><input type="checkbox" data-bulk-candidate-selected checked /></td>
      <td>${escapeHTML(candidate.sourceType)}</td>
      <td>
        <input data-bulk-library-id value="${escapeHTML(candidate.libraryId)}" />
        <input data-bulk-display-name value="${escapeHTML(candidate.displayName || "")}" placeholder="display name" />
      </td>
      <td>
        <input data-bulk-url value="${escapeHTML(candidate.url)}" />
        ${candidate.websiteBaseUrl ? `<input data-bulk-website-base-url value="${escapeHTML(candidate.websiteBaseUrl)}" />` : ""}
      </td>
      <td>${versionBlock}</td>
      <td>
        <select data-bulk-source-role>
          ${sourceRoleOption("primary_docs", "Primary docs", candidate.sourceRole)}
          ${sourceRoleOption("website_docs", "Website docs", candidate.sourceRole)}
          ${sourceRoleOption("llms_manifest", "LLMS manifest", candidate.sourceRole)}
          ${sourceRoleOption("api_reference", "API reference", candidate.sourceRole)}
          ${sourceRoleOption("manual_docs", "Manual docs", candidate.sourceRole)}
        </select>
      </td>
    </tr>
  `;
}

function bulkVersionOption(version: LibraryVersionCandidate) {
  const value = escapeHTML(JSON.stringify(version));
  const refType = version.refType || version.type;
  const label = version.label || version.ref;
  const meta = [refType, version.reason].filter(Boolean).join(" · ");
  return `
    <label class="bulk-version-option">
      <input type="checkbox" data-bulk-version-candidate value="${value}" ${version.recommended ? "checked" : ""} />
      <span>${escapeHTML(label)}</span>
      <em>${escapeHTML(meta)}</em>
    </label>
  `;
}

function sourceRoleOption(value: string, label: string, selected = "") {
  return `<option value="${value}" ${value === selected ? "selected" : ""}>${label}</option>`;
}

function selectedBulkCandidates() {
  if (!state.bulkResult) return [];
  const byIDMap = new Map(state.bulkResult.accepted.map((candidate) => [candidate.id, candidate]));
  const selected: BulkSourceCandidate[] = [];
  document.querySelectorAll<HTMLTableRowElement>("[data-bulk-candidate]").forEach((row) => {
    const checkbox = row.querySelector<HTMLInputElement>("[data-bulk-candidate-selected]");
    if (!checkbox?.checked) return;
    const candidate = byIDMap.get(row.dataset.bulkCandidate || "");
    if (!candidate) return;
    selected.push({
      ...candidate,
      libraryId: row.querySelector<HTMLInputElement>("[data-bulk-library-id]")?.value.trim() || candidate.libraryId,
      displayName: row.querySelector<HTMLInputElement>("[data-bulk-display-name]")?.value.trim() || candidate.displayName,
      url: row.querySelector<HTMLInputElement>("[data-bulk-url]")?.value.trim() || candidate.url,
      websiteBaseUrl: row.querySelector<HTMLInputElement>("[data-bulk-website-base-url]")?.value.trim() || candidate.websiteBaseUrl,
      sourceRole: row.querySelector<HTMLSelectElement>("[data-bulk-source-role]")?.value || candidate.sourceRole,
      versions: selectedBulkVersions(row, candidate)
    });
  });
  return selected;
}

function selectedBulkVersions(row: HTMLTableRowElement, candidate: BulkSourceCandidate) {
  if (candidate.sourceType !== "github_repo") return candidate.versions;
  const versions: LibraryVersionCandidate[] = [];
  const inputs = row.querySelectorAll<HTMLInputElement>("[data-bulk-version-candidate]");
  inputs.forEach((input) => {
    if (!input.checked) return;
    try {
      versions.push(JSON.parse(input.value) as LibraryVersionCandidate);
    } catch {
    }
  });
  return inputs.length ? versions.slice(0, 20) : candidate.versions;
}

async function submitBulkSources() {
  const candidates = selectedBulkCandidates();
  if (!candidates.length) return;
  switchLibraryWorkspace("bulkJobs");
  const output = byID("bulk-jobs");
  output.innerHTML = `<p class="empty">Creating bulk job...</p>`;
  try {
    const response = await api.bulkSubmit({ candidates, force: byID<HTMLInputElement>("bulk-force").checked });
    await loadBulkJobs();
    await loadRuns();
    await loadOverview();
    output.prepend(bulkJobNotice(response.job));
  } catch (error) {
    showCodeElement(output, error);
  }
}

function bulkJobNotice(job: BulkJob) {
  const node = document.createElement("div");
  node.className = "inspection-row";
  node.innerHTML = `${statusBadge(job.status)}<span>${escapeHTML(job.jobId)} queued ${job.total} sources.</span>`;
  return node;
}

async function loadBulkJobs() {
  state.bulkJobs = await api.bulkJobs();
  renderBulkJobs();
}

function renderBulkJobs() {
  const container = byID("bulk-jobs");
  if (!state.bulkJobs.length) {
    container.innerHTML = empty("No bulk jobs.");
    return;
  }
  container.innerHTML = `
    <div class="section-title">Bulk jobs</div>
    <div class="bulk-job-list">
      ${state.bulkJobs.slice(0, 8).map(bulkJobRow).join("")}
    </div>
  `;
  container.querySelectorAll<HTMLButtonElement>("[data-cancel-bulk-job]").forEach((button) => {
    button.addEventListener("click", () => void cancelBulkJob(button.dataset.cancelBulkJob || ""));
  });
  container.querySelectorAll<HTMLButtonElement>("[data-open-bulk-job]").forEach((button) => {
    button.addEventListener("click", () => void openBulkJobDetail(button.dataset.openBulkJob || ""));
  });
  container.querySelectorAll<HTMLButtonElement>("[data-open-run]").forEach((button) => {
    button.addEventListener("click", () => void selectRun(button.dataset.openRun || ""));
  });
  container.querySelectorAll<HTMLButtonElement>("[data-open-quarantine]").forEach((button) => {
    button.addEventListener("click", () => {
      state.selectedTab = "quarantine";
      void selectRun(button.dataset.openQuarantine || "");
    });
  });
  renderIcons();
}

function bulkJobRow(job: BulkJob) {
  const runCounts = job.runCounts;
  const runSummary = runCounts?.total
    ? `
      <div class="bulk-run-counts">
        ${miniCount("Runs", runCounts.total || 0)}
        ${miniCount("Queued", runCounts.queued || 0)}
        ${miniCount("Running", runCounts.running || 0)}
        ${miniCount("Published", runCounts.published || 0)}
        ${miniCount("Failed", runCounts.failed || 0)}
      </div>
      <div class="bulk-stage-counts">${Object.entries(runCounts.byStage || {}).map(([stage, count]) => `<span>${escapeHTML(stage)} ${count}</span>`).join("")}</div>
    `
    : "";
  const itemRows = job.items.slice(0, 40).map(bulkJobItemRow).join("");
  return `
    <article class="bulk-job-row">
      <div class="bulk-job-main">
        <div class="bulk-job-title">
          <strong>${escapeHTML(job.jobId)}</strong>
          <p>${escapeHTML(job.updatedAt)} · ${job.succeeded}/${job.total} submitted · ${job.failed} failed</p>
        </div>
        ${runSummary}
        <div class="bulk-job-items">${itemRows || empty("No bulk items.")}</div>
      </div>
      <div class="item-actions">
        ${statusBadge(job.status)}
        <button type="button" data-open-bulk-job="${escapeHTML(job.jobId)}" data-icon="search">Details</button>
        ${job.status === "queued" || job.status === "running" ? `<button type="button" data-cancel-bulk-job="${escapeHTML(job.jobId)}" data-icon="ban">Cancel</button>` : ""}
      </div>
    </article>
  `;
}

function bulkJobItemRow(item: BulkJobItem) {
  const runs = item.responses && item.responses.length ? item.responses : item.response?.runId ? [item.response] : [];
  const runButtons = runs.slice(0, 4).map((run) => `
    <button type="button" data-open-run="${escapeHTML(run.runId)}">${escapeHTML(run.version || "run")}</button>
  `).join("");
  const quarantineButton = runs[0]?.runId ? `<button type="button" data-open-quarantine="${escapeHTML(runs[0].runId)}">Quarantine</button>` : "";
  return `
    <div class="bulk-job-item">
      <div>
        <strong>${escapeHTML(item.candidate.libraryId)}</strong>
        <span>${escapeHTML(item.candidate.sourceType)} · ${escapeHTML(item.candidate.url)}</span>
        ${item.error ? `<em>${escapeHTML(item.error)}</em>` : ""}
      </div>
      <div class="bulk-run-actions">
        ${statusBadge(item.status)}
        ${runButtons}
        ${quarantineButton}
        ${runs.length > 4 ? `<span class="muted">+${runs.length - 4} runs</span>` : ""}
      </div>
    </div>
  `;
}

function miniCount(label: string, value: number) {
  return `<span><b>${value}</b>${escapeHTML(label)}</span>`;
}

async function cancelBulkJob(jobID: string) {
  if (!jobID) return;
  await api.cancelBulkJob(jobID, "canceled from admin");
  await loadBulkJobs();
  await loadOverview();
}

async function openBulkJobDetail(jobID: string) {
  if (!jobID) return;
  state.bulkDetailPage = 1;
  state.bulkDetailStatus = "";
  state.bulkDetailQuery = "";
  openModal("Bulk job detail", `<p class="empty">Loading bulk job...</p>`);
  try {
    renderBulkJobDetail(await api.bulkJob(jobID));
  } catch (error) {
    showCode("modal-body", error);
  }
}

function renderBulkJobDetail(job: BulkJob) {
  const items = filteredBulkItems(job);
  const pageSize = 25;
  const maxPage = Math.max(1, Math.ceil(items.length / pageSize));
  state.bulkDetailPage = Math.min(Math.max(state.bulkDetailPage, 1), maxPage);
  const start = (state.bulkDetailPage - 1) * pageSize;
  const pageItems = items.slice(start, start + pageSize);
  const counts = bulkJobStatusCounts(job);
  byID("modal-body").innerHTML = `
    <div class="bulk-detail">
      <div class="bulk-detail-head">
        <div>
          <strong>${escapeHTML(job.jobId)}</strong>
          <p>${escapeHTML(job.updatedAt)} · ${job.succeeded}/${job.total} submitted · ${job.failed} failed · ${job.canceled} canceled</p>
        </div>
        <div class="item-actions">
          ${statusBadge(job.status)}
          <button type="button" data-refresh-bulk-detail data-icon="refresh">Refresh</button>
          ${job.status === "queued" || job.status === "running" ? `<button type="button" data-cancel-bulk-job="${escapeHTML(job.jobId)}" data-icon="ban">Cancel</button>` : ""}
        </div>
      </div>
      <div class="bulk-run-counts wide">
        ${miniCount("Items", job.total || 0)}
        ${miniCount("Queued", counts.queued || 0)}
        ${miniCount("Running", counts.running || 0)}
        ${miniCount("Succeeded", counts.succeeded || 0)}
        ${miniCount("Failed", counts.failed || 0)}
        ${miniCount("Canceled", counts.canceled || 0)}
        ${miniCount("Runs", job.runCounts?.total || 0)}
      </div>
      ${job.runCounts?.byStage ? `<div class="bulk-stage-counts detail">${Object.entries(job.runCounts.byStage).map(([stage, count]) => `<span>${escapeHTML(stage)} ${count}</span>`).join("")}</div>` : ""}
      <div class="bulk-detail-controls">
        <input id="bulk-detail-query" placeholder="Filter by library, URL, error, run ID" value="${escapeHTML(state.bulkDetailQuery)}" />
        <select id="bulk-detail-status">
          <option value="">All items</option>
          ${["queued", "running", "succeeded", "failed", "canceled"].map((status) => `<option value="${status}" ${state.bulkDetailStatus === status ? "selected" : ""}>${status}</option>`).join("")}
        </select>
        <button type="button" data-apply-bulk-filter>Apply</button>
      </div>
      <div class="bulk-detail-list">
        ${pageItems.map(bulkJobDetailItemRow).join("") || empty("No items match the filters.")}
      </div>
      <div class="pagination">
        <button type="button" data-bulk-page="prev" ${state.bulkDetailPage <= 1 ? "disabled" : ""}>Previous</button>
        <span>${state.bulkDetailPage} / ${maxPage} · ${items.length} item(s)</span>
        <button type="button" data-bulk-page="next" ${state.bulkDetailPage >= maxPage ? "disabled" : ""}>Next</button>
      </div>
    </div>
  `;
  byID("modal-body").querySelector("[data-apply-bulk-filter]")?.addEventListener("click", () => {
    state.bulkDetailQuery = byID<HTMLInputElement>("bulk-detail-query").value;
    state.bulkDetailStatus = byID<HTMLSelectElement>("bulk-detail-status").value;
    state.bulkDetailPage = 1;
    renderBulkJobDetail(job);
  });
  byID<HTMLSelectElement>("bulk-detail-status").addEventListener("change", () => {
    state.bulkDetailQuery = byID<HTMLInputElement>("bulk-detail-query").value;
    state.bulkDetailStatus = byID<HTMLSelectElement>("bulk-detail-status").value;
    state.bulkDetailPage = 1;
    renderBulkJobDetail(job);
  });
  byID("modal-body").querySelector("[data-refresh-bulk-detail]")?.addEventListener("click", async () => {
    renderBulkJobDetail(await api.bulkJob(job.jobId));
  });
  byID("modal-body").querySelectorAll<HTMLButtonElement>("[data-bulk-page]").forEach((button) => {
    button.addEventListener("click", () => {
      state.bulkDetailPage += button.dataset.bulkPage === "next" ? 1 : -1;
      renderBulkJobDetail(job);
    });
  });
  byID("modal-body").querySelectorAll<HTMLButtonElement>("[data-cancel-bulk-job]").forEach((button) => {
    button.addEventListener("click", async () => {
      await cancelBulkJob(button.dataset.cancelBulkJob || "");
      renderBulkJobDetail(await api.bulkJob(job.jobId));
    });
  });
  byID("modal-body").querySelectorAll<HTMLButtonElement>("[data-open-run]").forEach((button) => {
    button.addEventListener("click", () => void selectRun(button.dataset.openRun || ""));
  });
  byID("modal-body").querySelectorAll<HTMLButtonElement>("[data-open-quarantine]").forEach((button) => {
    button.addEventListener("click", () => {
      state.selectedTab = "quarantine";
      void selectRun(button.dataset.openQuarantine || "");
    });
  });
  renderIcons();
}

function filteredBulkItems(job: BulkJob) {
  const query = state.bulkDetailQuery.trim().toLowerCase();
  return job.items.filter((item) => {
    if (state.bulkDetailStatus && item.status !== state.bulkDetailStatus) return false;
    if (!query) return true;
    const runs = item.responses && item.responses.length ? item.responses : item.response?.runId ? [item.response] : [];
    return [
      item.status,
      item.error || "",
      item.candidate.libraryId,
      item.candidate.sourceType,
      item.candidate.url,
      ...runs.map((run) => `${run.runId} ${run.version}`)
    ].join(" ").toLowerCase().includes(query);
  });
}

function bulkJobStatusCounts(job: BulkJob) {
  return job.items.reduce<Record<string, number>>((counts, item) => {
    counts[item.status || "queued"] = (counts[item.status || "queued"] || 0) + 1;
    return counts;
  }, {});
}

function bulkJobDetailItemRow(item: BulkJobItem) {
  const runs = item.responses && item.responses.length ? item.responses : item.response?.runId ? [item.response] : [];
  const runButtons = runs.map((run) => `
    <button type="button" data-open-run="${escapeHTML(run.runId)}">${escapeHTML(run.version || run.runId)}</button>
  `).join("");
  const quarantineButton = runs[0]?.runId ? `<button type="button" data-open-quarantine="${escapeHTML(runs[0].runId)}">Quarantine</button>` : "";
  return `
    <article class="bulk-detail-item">
      <div>
        <div class="bulk-detail-title">
          ${statusBadge(item.status)}
          <strong>${escapeHTML(item.candidate.libraryId)}</strong>
          <span>${escapeHTML(item.candidate.sourceType)}</span>
        </div>
        <p>${escapeHTML(item.candidate.url)}</p>
        ${item.error ? `<em>${escapeHTML(item.error)}</em>` : ""}
      </div>
      <div class="bulk-run-actions detail">
        ${runButtons}
        ${quarantineButton}
      </div>
    </article>
  `;
}

async function selectLibrary(libraryID: string) {
  state.selectedLibrary = libraryID;
  const profile = await api.library(libraryID);
  byID("library-detail").classList.remove("hidden");
  byID("library-title").textContent = `${profile.displayName} ${profile.libraryId}`;
  byID("library-summary").innerHTML = `
    ${summaryCell("Status", statusBadge(profile.status))}
    ${summaryCell("Default", escapeHTML(profile.defaultVersion || "-"))}
    ${profile.defaultVersionAlias ? summaryCell("Alias", escapeHTML(profile.defaultVersionAlias)) : ""}
    ${summaryCell("Units", formatCount(unitCount(profile)))}
    ${summaryCell("Tokens", formatCount(profile.tokenCount || 0))}
    ${summaryCell("Quality", profile.qualityScore ? profile.qualityScore.toFixed(2) : "-")}
    ${summaryCell("Published", escapeHTML(profile.lastPublishedAt || "-"))}
  `;
  await renderLibraryTab(profile);
  renderIcons();
}

async function switchLibraryTab(tab: LibraryTab) {
  state.libraryTab = tab;
  document.querySelectorAll("[data-library-tab]").forEach((node) => {
    node.classList.toggle("active", (node as HTMLElement).dataset.libraryTab === tab);
  });
  if (state.selectedLibrary) await selectLibrary(state.selectedLibrary);
}

async function renderLibraryTab(profile: LibraryProfile) {
  const body = byID("library-body");
  if (state.libraryTab === "overview") {
    body.innerHTML = `
      <div class="detail-grid">
        ${fieldView("Library ID", profile.libraryId)}
        ${fieldView("Display name", profile.displayName)}
        ${fieldView("Description", profile.description || "-")}
        ${fieldView("Visibility", profile.visibility)}
        ${fieldView("Units", formatCount(unitCount(profile)))}
        ${fieldView("Tokens", formatCount(profile.tokenCount || 0))}
        ${fieldView("Primary source", profile.primarySourceId || "-")}
        ${fieldView("Last run", profile.lastPublishedRunId || "-")}
      </div>
    `;
    return;
  }
  if (state.libraryTab === "configuration") {
    const sources = await api.librarySources(profile.libraryId);
    const primary = sources.find((row) => row.source.source_id === profile.primarySourceId)?.source || sources[0]?.source;
    body.innerHTML = `
      <div class="form-grid">
        <label>Display name<input id="edit-display-name" value="${escapeHTML(profile.displayName)}" /></label>
        <label>Description<textarea id="edit-description" class="short-text">${escapeHTML(profile.description || "")}</textarea></label>
        <label>Visibility<select id="edit-visibility">
          <option value="public" ${profile.visibility === "public" ? "selected" : ""}>Public</option>
          <option value="private" ${profile.visibility === "private" ? "selected" : ""}>Private</option>
        </select></label>
        <label>Default version<input id="edit-default-version" value="${escapeHTML(profile.defaultVersion || "")}" /></label>
        <label>Website base URL<input id="edit-base-url" value="${escapeHTML(primary?.websiteBaseUrl || "")}" /></label>
        <label>Redirect URL<input id="edit-redirect-url" value="${escapeHTML(profile.redirectUrl || "")}" /></label>
        <label>Rules<textarea id="edit-rules" class="short-text">${escapeHTML((profile.rules || []).join("\n"))}</textarea></label>
        <label>Include<textarea id="edit-include" class="short-text">${escapeHTML((primary?.crawl_scope.include || []).join("\n"))}</textarea></label>
        <label>Exclude<textarea id="edit-exclude" class="short-text">${escapeHTML((primary?.crawl_scope.exclude || []).join("\n"))}</textarea></label>
        <label class="check-row"><input id="edit-disallow" type="checkbox" ${profile.disallowIndexing ? "checked" : ""} /> Disallow indexing</label>
      </div>
      <div class="actions padded"><button id="save-library-config" class="primary" type="button" data-icon="check">Save configuration</button></div>
      <div class="danger-zone">
        <div>
          <strong>Delete library</strong>
          <p>Removes the profile, sources, runs, events, published state, keyword indexes, and vectors for this library.</p>
        </div>
        <button id="delete-library" class="danger" type="button" data-icon="trash">Delete library</button>
      </div>
      <pre id="library-config-result" class="code"></pre>
    `;
    byID("save-library-config").addEventListener("click", () => void saveLibraryConfig(profile.libraryId));
    byID("delete-library").addEventListener("click", () => openDeleteLibraryModal(profile));
    renderIcons();
    return;
  }
  if (state.libraryTab === "sources") {
    const sources = await api.librarySources(profile.libraryId);
    body.innerHTML = `
      <div class="source-manager">
        <div>
          <div class="section-title">Attached sources</div>
          <div id="library-sources-list" class="list">${sources.length ? "" : empty("No sources.")}</div>
        </div>
        <form id="attach-source-form" class="attach-source-form">
          <div class="section-title">Add source to this library</div>
          <label>Type<select id="attach-source-type">
            <option value="github_repo">GitHub repo</option>
            <option value="website">Website</option>
            <option value="llms_txt">llms.txt</option>
            <option value="openapi">OpenAPI</option>
            <option value="uploaded_docs">Uploaded docs</option>
          </select></label>
          <label>URL<input id="attach-url" placeholder="https://docs.example.com" /></label>
          <label class="attach-website">Website base URL<input id="attach-website-base-url" placeholder="https://docs.example.com/docs" /></label>
          <label>Role<select id="attach-source-role">
            <option value="">Auto</option>
            <option value="primary_docs">Primary docs</option>
            <option value="website_docs">Website docs</option>
            <option value="llms_manifest">LLMS manifest</option>
            <option value="api_reference">API reference</option>
            <option value="manual_docs">Manual docs</option>
          </select></label>
          <label>Priority<input id="attach-priority" type="number" min="1" placeholder="auto" /></label>
	          <label class="attach-non-github-version">Version label<input id="attach-version-label" placeholder="${escapeHTML(profile.defaultVersion || "latest")}" /></label>
	          <label class="attach-github">Default branch<input id="attach-ref" placeholder="detect default branch" /></label>
	          <p class="attach-github form-hint">Use the Versions tab to discover and queue branch or tag corpora after attaching the source.</p>
          <label class="attach-github">Include<textarea id="attach-include" class="short-text" placeholder="docs/**&#10;README.md"></textarea></label>
          <label class="attach-github">Exclude<textarea id="attach-exclude" class="short-text" placeholder="src/**&#10;test/**"></textarea></label>
          <label class="attach-website">Max pages<input id="attach-max-pages" type="number" min="1" placeholder="1000" /></label>
          <label class="attach-website check-row"><input id="attach-respect-robots" type="checkbox" checked /> Respect robots.txt</label>
          <button class="primary" type="submit" data-icon="play">Add and index</button>
          <pre id="attach-source-result" class="code compact"></pre>
        </form>
      </div>
    `;
    const list = byID("library-sources-list");
    for (const row of sources) list.appendChild(librarySourceManagerItem(profile.libraryId, row));
    byID("attach-source-type").addEventListener("change", handleAttachSourceTypeChange);
    byID("attach-source-form").addEventListener("submit", (event) => {
      event.preventDefault();
      void attachSourceToLibrary(profile.libraryId);
    });
    handleAttachSourceTypeChange();
    renderIcons();
    return;
  }
  if (state.libraryTab === "aggregate") {
    await renderAggregateTab(profile);
    return;
  }
  if (state.libraryTab === "versions") {
    body.innerHTML = `
      <div class="version-toolbar">
        <div>
          <div class="section-title">Version discovery</div>
          <p class="muted">For GitHub sources, discover branches and tags, then import and queue selected versions as isolated indexing runs.</p>
        </div>
        <div class="toolbar-controls">
          <label class="check-row"><input id="discover-prerelease" type="checkbox" /> Include prereleases</label>
          <button id="discover-versions" type="button" data-icon="search">Discover versions</button>
        </div>
      </div>
      <div id="version-discovery-result" class="version-discovery"></div>
      <div id="versions-list" class="list">${profile.versions?.length ? "" : empty("No versions.")}</div>
      <div class="version-form">
        <label>Label<input id="version-label" placeholder="v1.0.0 or release branch" /></label>
        <label>Ref type<select id="version-type"><option value="branch">Branch</option><option value="tag">Tag</option><option value="url">URL</option><option value="upload">Upload</option><option value="manual">Manual</option></select></label>
        <label>Ref<input id="version-ref" placeholder="git branch, git tag, URL, or upload key" /></label>
        <label>Title<input id="version-title" placeholder="Optional display title" /></label>
        <button id="validate-version" type="button" data-icon="check">Validate</button>
        <button id="add-version" class="primary" type="button" data-icon="play">Add version</button>
      </div>
      <pre id="version-validation-result" class="code compact"></pre>
    `;
    const list = byID("versions-list");
    for (const version of profile.versions || []) list.appendChild(versionItem(profile.libraryId, version));
    byID("discover-versions").addEventListener("click", () => void discoverVersions(profile.libraryId));
    byID("validate-version").addEventListener("click", () => void validateVersionForm(profile.libraryId));
    byID("add-version").addEventListener("click", () => void addVersion(profile.libraryId));
    renderIcons();
    return;
  }
  if (state.libraryTab === "indexing") {
    const runs = await api.libraryRuns(profile.libraryId);
    body.innerHTML = `<div id="library-runs-list" class="list">${runs.length ? "" : empty("No runs.")}</div>`;
    const list = byID("library-runs-list");
    for (const run of runs) list.appendChild(runItem(run));
    return;
  }
  if (state.libraryTab === "preview") {
    body.innerHTML = `
      <div class="inline-form">
        <input id="preview-query" placeholder="routing middleware example" />
        <select id="preview-type"><option value="json">JSON</option><option value="txt">Text</option></select>
        <button id="preview-search" class="primary" type="button" data-icon="search">Search</button>
      </div>
      <pre id="preview-output" class="code tall"></pre>
    `;
    byID("preview-search").addEventListener("click", () => void previewLibrary(profile.libraryId));
    renderIcons();
    return;
  }
  if (state.libraryTab === "quality") {
    showDetailCode(await api.libraryQuality(profile.libraryId));
    return;
  }
  if (state.libraryTab === "metrics") {
    showDetailCode(await api.libraryMetrics(profile.libraryId));
  }
}

async function saveLibraryConfig(libraryID: string) {
  const patch: LibraryProfilePatch = {
    displayName: byID<HTMLInputElement>("edit-display-name").value.trim(),
    description: byID<HTMLTextAreaElement>("edit-description").value.trim(),
    visibility: byID<HTMLSelectElement>("edit-visibility").value,
    defaultVersion: byID<HTMLInputElement>("edit-default-version").value.trim(),
    redirectUrl: byID<HTMLInputElement>("edit-redirect-url").value.trim(),
    disallowIndexing: byID<HTMLInputElement>("edit-disallow").checked,
    rules: linesFrom("edit-rules"),
    crawlScope: {
      include: linesFrom("edit-include"),
      exclude: linesFrom("edit-exclude"),
      websiteBaseUrl: byID<HTMLInputElement>("edit-base-url").value.trim()
    }
  };
  showCode("library-config-result", await api.updateLibrary(libraryID, patch));
  await loadLibraries();
  await selectLibrary(libraryID);
}

async function addVersion(libraryID: string) {
  const version: Partial<LibraryVersion> = {
    label: byID<HTMLInputElement>("version-label").value.trim(),
    refType: byID<HTMLSelectElement>("version-type").value as LibraryVersion["refType"],
    type: byID<HTMLSelectElement>("version-type").value as LibraryVersion["type"],
    ref: byID<HTMLInputElement>("version-ref").value.trim(),
    title: byID<HTMLInputElement>("version-title").value.trim()
  };
  await api.addLibraryVersion(libraryID, version);
  await selectLibrary(libraryID);
}

async function validateVersionForm(libraryID: string) {
  const version: Partial<LibraryVersion> = {
    label: byID<HTMLInputElement>("version-label").value.trim(),
    refType: byID<HTMLSelectElement>("version-type").value as LibraryVersion["refType"],
    type: byID<HTMLSelectElement>("version-type").value as LibraryVersion["type"],
    ref: byID<HTMLInputElement>("version-ref").value.trim(),
    title: byID<HTMLInputElement>("version-title").value.trim()
  };
  try {
    showCode("version-validation-result", await api.validateLibraryVersion(libraryID, version));
  } catch (error) {
    showCode("version-validation-result", error);
  }
}

async function discoverVersions(libraryID: string) {
  const includePrerelease = byID<HTMLInputElement>("discover-prerelease").checked;
  const container = byID("version-discovery-result");
  container.innerHTML = `<div class="muted padded">Discovering versions...</div>`;
  try {
    const discovery = await api.discoverLibraryVersions(libraryID, {
      limit: 1000,
      maxRecommended: 20,
      includePrerelease
    });
    container.innerHTML = renderVersionDiscovery(discovery.recommended, discovery.tags, discovery.branches, discovery.warnings || []);
    container.querySelectorAll<HTMLButtonElement>("[data-import-version]").forEach((button) => {
      button.addEventListener("click", () => {
        const raw = button.dataset.importVersion;
        if (!raw) return;
        void importDiscoveredVersion(libraryID, JSON.parse(raw) as LibraryVersionCandidate);
      });
    });
  } catch (error) {
    showCodeElement(container, error);
  }
  renderIcons();
}

function renderVersionDiscovery(recommended: LibraryVersionCandidate[], tags: LibraryVersionCandidate[], branches: LibraryVersionCandidate[], warnings: string[]) {
  const recommendedRows = recommended.map(versionCandidateItem).join("");
  const stableTags = tags.filter((item) => item.semver && !item.prerelease).slice(0, 20).map(versionCandidateItem).join("");
  const branchRows = branches.slice(0, 20).map(versionCandidateItem).join("");
  const warningText = warnings.length ? `<div class="warning-text">${warnings.map(escapeHTML).join("<br />")}</div>` : "";
  return `
    ${warningText}
    <div class="discovery-grid">
      <section>
        <div class="section-title">Recommended</div>
        <div class="mini-list">${recommendedRows || empty("No recommendations.")}</div>
      </section>
      <section>
        <div class="section-title">Stable tags</div>
        <div class="mini-list">${stableTags || empty("No stable semver tags found in the scanned range.")}</div>
      </section>
      <section>
        <div class="section-title">Branches</div>
        <div class="mini-list">${branchRows || empty("No branches found.")}</div>
      </section>
    </div>
  `;
}

function versionCandidateItem(candidate: LibraryVersionCandidate) {
  const disabled = candidate.configured ? "disabled" : "";
  const badges = [
    candidate.type,
    candidate.semver ? "semver" : "",
    candidate.prerelease ? "prerelease" : "",
    candidate.configured ? "configured" : ""
  ].filter(Boolean).join(" · ");
  return `
    <article class="candidate-row">
      <div>
        <strong>${escapeHTML(candidate.label)}</strong>
        <p>${escapeHTML(badges)}${candidate.reason ? ` · ${escapeHTML(candidate.reason)}` : ""}</p>
      </div>
      <button type="button" ${disabled} data-import-version="${escapeHTML(JSON.stringify(candidate))}" data-icon="play">${candidate.configured ? "Added" : "Import & index"}</button>
    </article>
  `;
}

async function importDiscoveredVersion(libraryID: string, candidate: LibraryVersionCandidate) {
  try {
    const profile = await api.addLibraryVersion(libraryID, {
      label: candidate.label,
      refType: candidate.refType,
      type: candidate.type,
      ref: candidate.ref,
      title: candidate.label,
      resolvedSha: candidate.resolvedSha,
      source: "github_discovery"
    });
    const version = (profile.versions || []).find((item) => item.label === candidate.label || item.ref === candidate.ref);
    if (version?.versionId) {
      showCode("version-validation-result", await api.refreshLibraryVersion(libraryID, version.versionId, false));
    }
    await selectLibrary(libraryID);
  } catch (error) {
    showCode("version-validation-result", error);
  }
}

async function deleteVersion(libraryID: string, versionID: string) {
  await api.deleteLibraryVersion(libraryID, versionID);
  await selectLibrary(libraryID);
}

function openDeleteLibraryModal(profile: LibraryProfile) {
  openModal("Delete library", `
    <div class="delete-confirm">
      <p>This permanently deletes <code>${escapeHTML(profile.libraryId)}</code> from Oz state and removes its vectors.</p>
      <label>Type library ID<input id="delete-library-confirm" autocomplete="off" spellcheck="false" placeholder="${escapeHTML(profile.libraryId)}" /></label>
      <div class="decision-actions">
        <button id="delete-library-cancel" type="button">Cancel</button>
        <button id="delete-library-confirm-button" class="danger" type="button" data-icon="trash" disabled>Delete library</button>
      </div>
      <pre id="delete-library-result" class="code compact"></pre>
    </div>
  `);
  const input = byID<HTMLInputElement>("delete-library-confirm");
  const button = byID<HTMLButtonElement>("delete-library-confirm-button");
  input.addEventListener("input", () => {
    button.disabled = input.value.trim() !== profile.libraryId;
  });
  byID("delete-library-cancel").addEventListener("click", closeModal);
  button.addEventListener("click", () => void deleteLibrary(profile.libraryId, input.value.trim()));
  input.focus();
  renderIcons();
}

async function deleteLibrary(libraryID: string, confirmation: string) {
  try {
    const result = await api.deleteLibrary(libraryID, confirmation);
    showCode("delete-library-result", result);
    state.selectedLibrary = "";
    byID("library-detail").classList.add("hidden");
    await Promise.all([loadLibraries(), loadSources(), loadRuns(), loadNotifications(), loadDeadLetters()]);
    closeModal();
  } catch (error) {
    showCode("delete-library-result", error);
  }
}

async function validateVersion(libraryID: string, versionID: string) {
  showDetailCode(await api.validateExistingLibraryVersion(libraryID, versionID));
}

async function refreshVersion(libraryID: string, versionID: string) {
  showDetailCode(await api.refreshLibraryVersion(libraryID, versionID, false));
  await Promise.all([selectLibrary(libraryID), loadRuns()]);
}

async function previewLibrary(libraryID: string) {
  const query = byID<HTMLInputElement>("preview-query").value.trim();
  const type = byID<HTMLSelectElement>("preview-type").value;
  showCode("preview-output", await api.previewLibrary(libraryID, query, type));
}

async function refreshSelectedLibrary(force: boolean) {
  if (!state.selectedLibrary) {
    openModal("No library selected", `<p>Select a library before refreshing.</p>`);
    return;
  }
  showDetailCode(await api.refreshLibrary(state.selectedLibrary, force));
  await Promise.all([loadOverview(), loadLibraries(), loadRuns()]);
}

async function openSelectedLibraryDeleteModal() {
  if (!state.selectedLibrary) {
    openModal("No library selected", `<p>Select a library before deleting.</p>`);
    return;
  }
  openDeleteLibraryModal(await api.library(state.selectedLibrary));
}

async function loadSources() {
  const sources = await api.sources();
  state.sources = sources;
  const list = byID("sources-list");
  list.innerHTML = sources.length ? "" : empty("No sources.");
  for (const record of sources) list.appendChild(sourceItem(record));
  renderRefreshTiers();
  renderIcons();
}

async function loadRefreshTiers() {
  const [sources, usage, config] = await Promise.all([
    api.sources(),
    api.usage(),
    api.systemConfig()
  ]);
  state.sources = sources;
  state.usage = usage;
  state.systemConfig = config;
  renderRefreshTiers();
}

async function loadAdmissionsQueue() {
  const [stats, admissions] = await Promise.all([
    api.admissionStats(),
    api.admissions({
      status: state.admissionStatusFilter,
      intent: state.admissionIntentFilter,
      urgency: state.admissionUrgencyFilter,
      limit: 250
    })
  ]);
  state.admissionStats = stats;
  state.admissions = admissions;
  renderAdmissionsQueue();
}

async function dispatchAdmissions() {
  try {
    state.admissionStats = await api.dispatchAdmissions();
    state.admissions = await api.admissions({
      status: state.admissionStatusFilter,
      intent: state.admissionIntentFilter,
      urgency: state.admissionUrgencyFilter,
      limit: 250
    });
    renderAdmissionsQueue();
  } catch (error) {
    openModal("Dispatch failed", `<pre>${escapeHTML(error instanceof Error ? error.message : String(error))}</pre>`);
  }
}

function renderAdmissionsQueue() {
  const stats = state.admissionStats;
  const counters = byID("admission-counters");
  if (!stats) {
    counters.innerHTML = "";
    byID("admissions-list").innerHTML = empty("No queue data loaded.");
    return;
  }
  counters.innerHTML = `
    ${admissionCounter("Queued", stats.queued, "waiting for admission")}
    ${admissionCounter("Admitted", stats.admitted, "inside NATS pipeline")}
    ${admissionCounter("Running", stats.running, "active refreshes")}
    ${admissionCounter("Priority active", stats.priorityActive, "hard cap slot use")}
    ${admissionCounter("Global active", stats.globalActive, "all admitted/running")}
    ${admissionCounter("New / hour", stats.newIngestHourly, "new ingest admissions")}
    ${admissionCounter("Refresh / hour", stats.refreshHourly, "refresh admissions")}
    ${admissionCounter("Migration / hour", stats.migrationHourly, "schema backfill")}
  `;
  const list = byID("admissions-list");
  byID("admission-list-count").textContent = `${state.admissions.length} shown`;
  list.innerHTML = state.admissions.length ? "" : empty("No matching admissions.");
  for (const admission of state.admissions) list.appendChild(admissionItem(admission));
  renderIcons();
}

function admissionCounter(label: string, value: number, hint: string) {
  return `
    <button type="button" class="counter-card">
      <span>${escapeHTML(label)}</span>
      <strong>${formatCount(value)}</strong>
      <em>${escapeHTML(hint)}</em>
    </button>
  `;
}

function admissionItem(admission: IngestAdmission) {
  const item = document.createElement("article");
  item.className = "list-item";
  const runs = admission.runIds || [];
  const sourceURL = admission.source?.url || "-";
  item.innerHTML = `
    <div>
      <strong>${escapeHTML(admission.libraryId || admission.sourceId)}</strong>
      <p>${escapeHTML(admission.intentType)} · ${escapeHTML(admission.urgency)} · ${escapeHTML(admission.reason || "-")}</p>
      <p>${escapeHTML(admission.sourceId)} · ${escapeHTML(admission.version || "-")} · ${escapeHTML(sourceURL)}</p>
      <p>created ${escapeHTML(admission.createdAt || "-")} · updated ${escapeHTML(admission.updatedAt || "-")}</p>
      ${admission.lastError ? `<p class="error-text">${escapeHTML(admission.lastError)}</p>` : ""}
    </div>
    <div class="item-actions">
      ${statusBadge(admission.status)}
      ${runs.length ? `<button type="button" data-open-run="${escapeHTML(runs[0])}" data-icon="search">Run</button>` : ""}
      ${admission.urgency !== "priority" && admission.status === "queued" ? `<button type="button" data-promote data-icon="upload">Priority</button>` : ""}
      ${admission.status === "failed" || admission.status === "canceled" ? `<button type="button" data-retry data-icon="rotate">Retry</button>` : ""}
      ${admission.status === "queued" || admission.status === "admitted" || admission.status === "running" ? `<button type="button" class="danger" data-cancel data-icon="ban">Cancel</button>` : ""}
    </div>
  `;
  item.querySelector<HTMLButtonElement>("[data-promote]")?.addEventListener("click", () => void promoteAdmission(admission.admissionId));
  item.querySelector<HTMLButtonElement>("[data-retry]")?.addEventListener("click", () => void retryAdmission(admission.admissionId));
  item.querySelector<HTMLButtonElement>("[data-cancel]")?.addEventListener("click", () => void cancelAdmission(admission.admissionId));
  item.querySelector<HTMLButtonElement>("[data-open-run]")?.addEventListener("click", (event) => {
    const runID = (event.currentTarget as HTMLButtonElement).dataset.openRun || "";
    if (runID) void selectRun(runID);
  });
  return item;
}

async function promoteAdmission(admissionID: string) {
  try {
    await api.promoteAdmission(admissionID);
    await loadAdmissionsQueue();
  } catch (error) {
    openModal("Priority update failed", `<pre>${escapeHTML(error instanceof Error ? error.message : String(error))}</pre>`);
  }
}

async function retryAdmission(admissionID: string) {
  try {
    await api.retryAdmission(admissionID);
    await loadAdmissionsQueue();
  } catch (error) {
    openModal("Retry failed", `<pre>${escapeHTML(error instanceof Error ? error.message : String(error))}</pre>`);
  }
}

async function cancelAdmission(admissionID: string) {
  try {
    await api.cancelAdmission(admissionID, "admin canceled");
    await Promise.all([loadAdmissionsQueue(), loadRuns()]);
  } catch (error) {
    openModal("Cancel failed", `<pre>${escapeHTML(error instanceof Error ? error.message : String(error))}</pre>`);
  }
}

async function submitSource() {
  try {
    const body = JSON.parse(byID<HTMLTextAreaElement>("source-json").value) as SubmitSourceRequest;
    showCode("source-result", await api.submitSource(body));
    await Promise.all([loadOverview(), loadLibraries(), loadSources(), loadRuns()]);
  } catch (error) {
    showCode("source-result", error);
  }
}

function formatSource() {
  const input = byID<HTMLTextAreaElement>("source-json");
  input.value = JSON.stringify(JSON.parse(input.value), null, 2);
}

async function refreshSource(source: ManagedSource, force: boolean) {
  showCode("source-result", await api.refreshSource(source.source_id, force));
  await Promise.all([loadLibraries(), loadSources(), loadRuns()]);
}

function handleAttachSourceTypeChange() {
  const type = byID<HTMLSelectElement>("attach-source-type").value as SourceType;
  const isGitHub = type === "github_repo";
  const isWebsite = type === "website";
  document.querySelectorAll<HTMLElement>(".attach-github").forEach((node) => node.classList.toggle("hidden", !isGitHub));
  document.querySelectorAll<HTMLElement>(".attach-website").forEach((node) => node.classList.toggle("hidden", !isWebsite));
  document.querySelectorAll<HTMLElement>(".attach-non-github-version").forEach((node) => node.classList.toggle("hidden", isGitHub));
  byID<HTMLSelectElement>("attach-source-role").value = defaultRoleForSourceType(type);
}

async function attachSourceToLibrary(libraryID: string) {
  const sourceType = byID<HTMLSelectElement>("attach-source-type").value as SourceType;
  const isGitHub = sourceType === "github_repo";
  const isWebsite = sourceType === "website";
  const refType = isGitHub ? "branch" : sourceRefType(sourceType);
  const ref = isGitHub
    ? byID<HTMLInputElement>("attach-ref").value.trim()
    : isWebsite
      ? byID<HTMLInputElement>("attach-website-base-url").value.trim() || byID<HTMLInputElement>("attach-url").value.trim()
      : byID<HTMLInputElement>("attach-url").value.trim();
  const request: CreateLibraryRequest = {
    sourceType,
    sourceRole: byID<HTMLSelectElement>("attach-source-role").value || undefined,
    aggregatePriority: numberValue("attach-priority"),
    url: byID<HTMLInputElement>("attach-url").value.trim(),
    websiteBaseUrl: isWebsite ? byID<HTMLInputElement>("attach-website-base-url").value.trim() : undefined,
    libraryId: libraryID,
    versionLabel: isGitHub ? undefined : byID<HTMLInputElement>("attach-version-label").value.trim() || undefined,
    refType,
    ref,
    crawlScope: {
      include: isGitHub ? linesFrom("attach-include") : undefined,
      exclude: isGitHub ? linesFrom("attach-exclude") : undefined,
      maxPages: isWebsite ? numberValue("attach-max-pages") : undefined,
      respectRobots: isWebsite ? byID<HTMLInputElement>("attach-respect-robots").checked : undefined
    },
    versionPolicy: {
      default_branch: isGitHub ? ref || undefined : undefined,
      semver: isGitHub
    },
    refreshPolicy: { mode: "webhook_plus_periodic", priority: "normal" }
  };
  try {
    showCode("attach-source-result", await api.addLibrarySource(libraryID, request));
    await Promise.all([loadLibraries(), loadSources(), loadRuns()]);
    await selectLibrary(libraryID);
  } catch (error) {
    showCode("attach-source-result", error);
  }
}

async function saveLibrarySource(libraryID: string, sourceID: string) {
  const patch: LibrarySourcePatch = {
    sourceRole: byID<HTMLSelectElement>(`source-role-${cssID(sourceID)}`).value,
    aggregatePriority: numberValue(`source-priority-${cssID(sourceID)}`),
    refreshPriority: byID<HTMLSelectElement>(`source-refresh-priority-${cssID(sourceID)}`).value,
    disabled: byID<HTMLInputElement>(`source-disabled-${cssID(sourceID)}`).checked,
    excludeFromAggregate: byID<HTMLInputElement>(`source-exclude-agg-${cssID(sourceID)}`).checked,
    websiteBaseUrl: byID<HTMLInputElement>(`source-base-${cssID(sourceID)}`).value.trim(),
    crawlScope: {
      include: linesFrom(`source-include-${cssID(sourceID)}`),
      exclude: linesFrom(`source-exclude-${cssID(sourceID)}`)
    }
  };
  showDetailCode(await api.updateLibrarySource(libraryID, sourceID, patch));
  await Promise.all([loadLibraries(), loadSources()]);
  await selectLibrary(libraryID);
}

async function refreshLibrarySource(libraryID: string, sourceID: string, force: boolean) {
  showDetailCode(await api.refreshLibrarySource(libraryID, sourceID, force));
  await Promise.all([loadOverview(), loadLibraries(), loadRuns()]);
}

async function renderAggregateTab(profile: LibraryProfile) {
  const body = byID("library-body");
  try {
    const aggregate = await api.libraryAggregate(profile.libraryId, profile.defaultVersion || "");
    body.innerHTML = aggregateView(profile, aggregate);
  } catch (error) {
    body.innerHTML = `
      <div class="aggregate-empty">
        <p>${escapeHTML(error instanceof Error ? error.message : String(error))}</p>
        <button id="rebuild-aggregate" class="primary" type="button" data-icon="refresh">Build aggregate</button>
      </div>
    `;
  }
  byID("rebuild-aggregate")?.addEventListener("click", () => void rebuildAggregate(profile.libraryId, profile.defaultVersion || ""));
  renderIcons();
}

async function rebuildAggregate(libraryID: string, version: string) {
  try {
    showDetailCode(await api.rebuildLibraryAggregate(libraryID, version));
    await loadLibraries();
  } catch (error) {
    showDetailCode(error);
  }
}

async function loadRuns() {
  const status = byID<HTMLSelectElement>("run-status").value;
  const stage = byID<HTMLSelectElement>("run-stage").value;
  const runs = await api.runs(status, stage);
  const visible = new Set(runs.filter(isCancelableRun).map((run) => run.runId));
  state.selectedRunIDs.forEach((runID) => {
    if (!visible.has(runID)) state.selectedRunIDs.delete(runID);
  });
  renderRunList("runs-list", runs);
  renderRunCounters();
}

async function selectRun(runID: string) {
  state.selectedRun = runID;
  switchView("runs");
  byID("run-detail").classList.remove("hidden");
  const run = await api.run(runID);
  byID("run-title").textContent = `${run.libraryId || run.runId} / ${run.version || "-"}`;
  renderTimeline(run);
  await loadRunTab();
  connectRunStream();
}

async function loadRunTab() {
  if (!state.selectedRun) return;
  try {
    switch (state.selectedTab) {
      case "events":
        showCode("run-output", await api.runEvents(state.selectedRun));
        break;
      case "diagnostics":
        showCode("run-output", await api.runDiagnostics(state.selectedRun));
        break;
      case "artifacts":
        renderArtifacts(await api.runArtifacts(state.selectedRun));
        break;
      case "crawl":
        showCode("run-output", await api.runCrawlAudit(state.selectedRun));
        break;
      case "quarantine":
        renderQuarantine(await api.runQuarantine(state.selectedRun));
        break;
    }
  } catch (error) {
    showCode("run-output", error);
  }
}

async function retryRun() {
  if (!state.selectedRun) {
    openModal("No run selected", `<p>Select a run before retrying.</p>`);
    return;
  }
  showCode("run-output", await api.retryRun(state.selectedRun));
  await Promise.all([loadRuns(), selectRun(state.selectedRun)]);
}

async function retryStage(stage: StageName) {
  if (!state.selectedRun) {
    openModal("No run selected", `<p>Select a run before retrying a stage.</p>`);
    return;
  }
  showCode("run-output", await api.retryStage(state.selectedRun, stage));
  await Promise.all([loadRuns(), selectRun(state.selectedRun)]);
}

async function cancelRun() {
  if (!state.selectedRun) {
    openModal("No run selected", `<p>Select a run before canceling.</p>`);
    return;
  }
  const reason = window.prompt("Cancel reason", "canceled from admin");
  if (reason === null) return;
  showCode("run-output", await api.cancelRun(state.selectedRun, reason));
  await Promise.all([loadRuns(), selectRun(state.selectedRun)]);
}

async function cancelSelectedRuns() {
  const runIDs = [...state.selectedRunIDs];
  if (!runIDs.length) {
    openModal("No runs selected", `<p>Select one or more queued or running runs before canceling.</p>`);
    return;
  }
  const reason = window.prompt("Cancel reason", "canceled from admin");
  if (reason === null) return;
  for (const runID of runIDs) {
    await api.cancelRun(runID, reason);
  }
  state.selectedRunIDs.clear();
  await Promise.all([loadRuns(), loadOverview()]);
}

async function loadDeadLetters() {
  const dead = await api.deadLetters();
  const list = byID("dead-list");
  list.innerHTML = dead.length ? "" : empty("No dead letters.");
  for (const row of dead) list.appendChild(deadLetterItem(row));
  renderIcons();
}

async function loadSystem(kind: "all" | "nats" | "qdrant" | "retrieval" | "embeddings" | "config") {
  const result =
    kind === "nats"
      ? await api.systemNATS()
      : kind === "qdrant"
        ? await api.systemQdrant()
        : kind === "retrieval"
        ? await api.systemRetrieval()
        : kind === "embeddings"
        ? await api.systemEmbeddings()
        : kind === "config"
        ? await api.systemConfig()
        : await api.system();
  if (kind === "config") state.systemConfig = objectValue(result);
  if (kind === "all") state.systemConfig = objectValue(objectValue(result).state);
  if (kind === "all") renderSystemHealth(result);
  else renderSystemSubset(kind, result);
  showCode("system-output", result);
}

function renderSystemHealth(result: unknown) {
  const node = byID("system-health");
  const payload = (result && typeof result === "object" ? result : {}) as Record<string, unknown>;
  const nats = objectValue(payload.nats);
  const qdrant = objectValue(payload.qdrant);
  const retrieval = objectValue(payload.retrieval);
  const stateConfig = objectValue(payload.state);
  const embedding = objectValue(payload.embedding);
  const prompt = objectValue(payload.promptInjection);
  node.innerHTML = `
    ${healthCard("State", String(stateConfig.stateBackend || "-"), `table ${stateConfig.stateTablePrefix || "-"}`)}
    ${healthCard("NATS", String(nats.status || "unknown"), String(nats.stream || "-"))}
    ${healthCard("Qdrant", String(qdrant.status || "unknown"), String(qdrant.collection || "-"))}
    ${healthCard("Retrieval", String(retrieval.status || "unknown"), `${retrieval.queryProvider || "-"} · ${retrieval.collection || "-"}`)}
    ${healthCard("Embedding", String(embedding.mode || "-"), `${embedding.indexProvider || "-"} index · ${embedding.queryProvider || "-"} query`)}
    ${healthCard("Safety LLM", prompt.llmEnabled ? "enabled" : "disabled", String(prompt.model || "-"))}
    ${healthCard("Discover timeout", `${stateConfig.discoverStageTimeoutMs || "-"} ms`, "per run")}
  `;
}

function objectValue(value: unknown) {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function renderSystemSubset(kind: "nats" | "qdrant" | "retrieval" | "embeddings" | "config", result: unknown) {
  const node = byID("system-health");
  const payload = objectValue(result);
  if (kind === "nats") {
    node.innerHTML = `
      ${healthCard("NATS", String(payload.status || "unknown"), String(payload.stream || "-"))}
      ${healthCard("Consumers", String(payload.consumers || "-"), "configured workers")}
    `;
  } else if (kind === "qdrant") {
    node.innerHTML = `
      ${healthCard("Qdrant", String(payload.status || "unknown"), String(payload.collection || "-"))}
      ${healthCard("Points", String(payload.points || "-"), "current collection")}
    `;
  } else if (kind === "retrieval") {
    node.innerHTML = `
      ${healthCard("Retrieval", String(payload.status || "unknown"), `${payload.queryProvider || "-"} · ${payload.queryModel || "-"}`)}
      ${healthCard("Qdrant", String(payload.qdrantStatus || "unknown"), String(payload.collection || "-"))}
      ${healthCard("Pools", `${payload.vectorPoolSize || "-"} vector · ${payload.keywordPoolSize || "-"} keyword`, `${payload.mergedPoolSize || "-"} merged · top ${payload.topK || "-"}`)}
      ${healthCard("Rerank", payload.rerankEnabled ? "enabled" : "disabled", `${payload.rerankMaxChars || "-"} max chars`)}
    `;
  } else if (kind === "embeddings") {
    const claw = objectValue(payload.claw);
    const fireworks = objectValue(payload.fireworks);
    const voyage = objectValue(payload.voyage);
    const pools = objectValue(payload.indexPools);
    const clawHealth = arrayValue(claw.health);
    node.innerHTML = `
      ${healthCard("Mode", String(payload.mode || "-"), `${payload.dimension || "-"} dimensions`)}
      ${healthCard("Index", String(payload.indexProvider || "-"), String(payload.indexModel || "-"))}
      ${healthCard("Query", String(payload.queryProvider || "-"), String(payload.queryModel || "-"))}
      ${healthCard("Fireworks", String(fireworks.status || "unknown"), String(fireworks.model || "-"))}
      ${healthCard("Voyage legacy", String(voyage.status || "unknown"), String(voyage.model || "-"))}
      ${clawHealth.length ? clawHealth.map((row, index) => {
        const item = objectValue(row);
        return healthCard(`Claw ${index + 1}`, String(item.status || "unknown"), `${item.latencyMs || "-"} ms · ${item.url || "-"}`);
      }).join("") : healthCard("Claw", "missing_urls", "CLAW_EMBED_URLS")}
      <div class="embedding-pool-controls">
        <div>
          <strong>Index workers</strong>
          <span>Changes apply before the next library is claimed.</span>
        </div>
        ${embeddingPoolSwitch("claw", "Claw", Boolean(pools.clawEnabled), arrayValue(claw.urls).length > 0, `${claw.batchSize || "-"} units / request`)}
        ${embeddingPoolSwitch(
          "fireworks",
          "Fireworks",
          Boolean(pools.fireworksEnabled),
          Boolean(fireworks.apiKeySet),
          `${fireworks.batchSize || "-"} units · ${formatCount(Number(fireworks.batchMaxEstimatedTokens) || 0)} tokens / request`
        )}
        <span class="embedding-pool-updated">${pools.updatedAt ? `Updated ${escapeHTML(String(pools.updatedAt))}` : ""}</span>
      </div>
    `;
    bindEmbeddingPoolControls(node);
  } else {
    node.innerHTML = `
      ${healthCard("State", String(payload.stateBackend || "-"), `table ${payload.stateTablePrefix || "-"}`)}
      ${healthCard("Config", "loaded", "runtime configuration")}
    `;
  }
}

function embeddingPoolSwitch(provider: string, label: string, enabled: boolean, configured: boolean, detail: string) {
  return `
    <label class="embedding-pool-switch ${configured ? "" : "unavailable"}">
      <input type="checkbox" role="switch" data-embedding-pool="${provider}" ${enabled ? "checked" : ""} ${configured ? "" : "disabled"} />
      <span>
        <strong>${escapeHTML(label)}</strong>
        <em>${escapeHTML(configured ? detail : "Not configured")}</em>
      </span>
    </label>
  `;
}

function bindEmbeddingPoolControls(root: HTMLElement) {
  root.querySelectorAll<HTMLInputElement>("[data-embedding-pool]").forEach((input) => {
    input.addEventListener("change", () => void updateEmbeddingPools(root));
  });
}

async function updateEmbeddingPools(root: HTMLElement) {
  const claw = root.querySelector<HTMLInputElement>('[data-embedding-pool="claw"]');
  const fireworks = root.querySelector<HTMLInputElement>('[data-embedding-pool="fireworks"]');
  if (!claw || !fireworks) return;
  root.querySelectorAll<HTMLInputElement>("[data-embedding-pool]").forEach((input) => {
    input.disabled = true;
  });
  try {
    await api.updateEmbeddingPools(claw.checked, fireworks.checked);
    await loadSystem("embeddings");
  } catch (error) {
    openModal("Embedding workers", `<p>${escapeHTML(error instanceof Error ? error.message : String(error))}</p>`);
    await loadSystem("embeddings");
  }
}

function arrayValue(value: unknown) {
  return Array.isArray(value) ? value : [];
}

function healthCard(label: string, value: string, hint: string) {
  const normalized = value.toLowerCase();
  const statusClass = normalized === "ok" || normalized === "enabled" ? "ok" : normalized.includes("unavailable") || normalized.includes("error") ? "bad" : "";
  return `<div class="health-card ${statusClass}"><span>${escapeHTML(label)}</span><strong>${escapeHTML(value)}</strong><em>${escapeHTML(hint)}</em></div>`;
}

function renderRunList(targetID: string, runs: RunState[]) {
  const list = byID(targetID);
  list.innerHTML = runs.length ? "" : empty("No runs.");
  for (const run of runs) list.appendChild(runItem(run));
}

function renderTimeline(run: RunState) {
  const timeline = byID("stage-timeline");
  timeline.innerHTML = "";
  if (run.progress) {
    const progressNode = document.createElement("div");
    progressNode.className = "timeline-progress";
    progressNode.innerHTML = progressBlock(run.progress);
    timeline.appendChild(progressNode);
  }
  for (const stage of stages) {
    const stateForStage = run.stages[stage] || { stage, status: "unknown", attempts: 0 };
    const node = document.createElement("div");
    node.className = "stage";
    node.innerHTML = `
      <div class="stage-head">
        <strong>${stage}</strong>
        ${statusBadge(stateForStage.status)}
      </div>
      <div class="muted">attempts ${stateForStage.attempts || 0}</div>
      ${stateForStage.error ? `<div class="error-text">${escapeHTML(stateForStage.error)}</div>` : ""}
      <button type="button" data-icon="rotate">Retry</button>
    `;
    node.querySelector("button")!.addEventListener("click", () => void retryStage(stage));
    timeline.appendChild(node);
  }
  renderIcons();
}

function renderArtifacts(rows: AdminArtifactSummary[]) {
  const output = byID("run-output");
  output.classList.remove("quarantine-output");
  output.innerHTML = rows.length ? "" : "No artifacts.";
  for (const row of rows) {
    const button = document.createElement("button");
    button.className = "artifact-row";
    button.type = "button";
    button.textContent = `${row.name} (${row.type}, ${row.bytes} bytes${row.rows ? `, ${row.rows} rows` : ""})`;
    button.addEventListener("click", async () => {
      showCode("run-output", await api.runArtifact(state.selectedRun, row.name));
    });
    output.appendChild(button);
  }
}

function renderQuarantine(report: QuarantineReport) {
  const output = byID("run-output");
  state.quarantineSelection.clear();
  const pendingPromptRows = report.rows.filter((row) => row.reason === "prompt_injection" && row.decision?.action !== "approve");
  output.classList.add("quarantine-output");
  output.innerHTML = `
    <div class="quarantine-head">
      <div>
        <strong>${report.total} quarantined</strong>
        <span>${report.pending} pending · ${report.approved} approved · ${report.rejected} rejected · ${report.promptInjection} prompt alerts</span>
      </div>
      <div class="filters">
        <button id="quarantine-analyze" type="button" data-icon="search" ${pendingPromptRows.length ? "" : "disabled"}>Analyze with LLM</button>
        <button id="quarantine-approve" class="primary" type="button" data-icon="check">Approve selected and retry embed</button>
        <button id="quarantine-reject" type="button" data-icon="ban">Reject selected</button>
      </div>
    </div>
    ${report.summary ? quarantineSummaryBlock(report.summary) : ""}
    <div class="quarantine-list">
      ${report.rows.length ? report.rows.map(quarantineRowHTML).join("") : empty("No quarantined snippets.")}
    </div>
  `;
  byID("quarantine-analyze")?.addEventListener("click", () => void analyzeSelectedQuarantine(report));
  byID("quarantine-approve")?.addEventListener("click", () => void decideSelectedQuarantine("approve", true));
  byID("quarantine-reject")?.addEventListener("click", () => void decideSelectedQuarantine("reject", false));
  output.querySelectorAll<HTMLInputElement>("[data-quarantine-row]").forEach((checkbox) => {
    checkbox.addEventListener("change", () => {
      if (checkbox.checked) state.quarantineSelection.add(checkbox.value);
      else state.quarantineSelection.delete(checkbox.value);
    });
  });
  renderIcons();
}

function quarantineSummaryBlock(summary: QuarantineLLMSummary) {
  return `
    <section class="quarantine-summary">
      <div>
        <strong>LLM summary</strong>
        <p>${escapeHTML(summary.summary || "-")}</p>
      </div>
      <span>${escapeHTML(summary.model)} · ${escapeHTML(summary.overallRisk || "unknown")}</span>
    </section>
  `;
}

function quarantineRowHTML(row: QuarantineRow) {
  const snippet = row.snippet || {};
  const title = snippet.title || snippet.codeTitle || snippet.infoTitle || snippet.id || row.rowId;
  const source = snippet.sourceUrl || snippet.pageId || snippet.sourcePath || "-";
  const text = snippet.sourceText || snippet.displayText || snippet.text || "";
  const review = row.promptInjectionReview || {};
  const signal = typeof review.signal === "object" && review.signal ? (review.signal as Record<string, unknown>) : {};
  const decision = row.decision?.action || "pending";
  return `
    <article class="quarantine-row">
      <label class="quarantine-check">
        <input type="checkbox" value="${escapeHTML(row.rowId)}" data-quarantine-row ${decision === "approve" ? "disabled" : ""} />
      </label>
      <div class="quarantine-main">
        <div class="quarantine-title">
          <strong>${escapeHTML(title)}</strong>
          ${statusBadge(row.reason)}
          ${statusBadge(decision)}
        </div>
        <p>${escapeHTML(source)}</p>
        <p>${escapeHTML(snippet.type || "-")} · ${escapeHTML(snippet.lane || "-")} · ${escapeHTML(String(signal["matched"] || ""))}</p>
        <pre>${escapeHTML(String(text).slice(0, 900))}</pre>
        ${row.decision ? `<p class="decision-line">Decision: ${escapeHTML(row.decision.action)} by ${escapeHTML(row.decision.actor)} — ${escapeHTML(row.decision.reason)}</p>` : ""}
      </div>
    </article>
  `;
}

async function analyzeSelectedQuarantine(report: QuarantineReport) {
  if (!state.selectedRun) return;
  const rowIds = selectedQuarantineRows(report);
  const summary = await api.analyzeRunQuarantine(state.selectedRun, [], rowIds);
  openModal("LLM quarantine analysis", `
    <div class="modal-section">
      <strong>${escapeHTML(summary.overallRisk || "unknown")} risk</strong>
      <p>${escapeHTML(summary.summary || "-")}</p>
    </div>
    <div class="recommendation-list">
      ${summary.recommended.map((item) => `
        <div class="recommendation-row">
          ${statusBadge(item.recommendation)}
          <span>${escapeHTML(item.snippetId || item.rowId)}</span>
          <p>${escapeHTML(item.rationale || "")}</p>
        </div>
      `).join("") || empty("No recommendations returned.")}
    </div>
  `);
  await loadRunTab();
}

async function decideSelectedQuarantine(action: "approve" | "reject", retryFromEmbed: boolean) {
  if (!state.selectedRun) return;
  const rowIds = Array.from(state.quarantineSelection);
  if (rowIds.length === 0) {
    openModal("No rows selected", `<p>Select one or more quarantine rows first.</p>`);
    return;
  }
  const reason = await requestDecisionReason(action);
  if (reason === null) return;
  const result = await api.decideRunQuarantine(state.selectedRun, action, [], rowIds, reason, retryFromEmbed);
  openModal("Quarantine decision saved", `<pre>${escapeHTML(JSON.stringify(result, null, 2))}</pre>`);
  await Promise.all([loadRuns(), loadRunTab()]);
}

function requestDecisionReason(action: "approve" | "reject") {
  return new Promise<string | null>((resolve) => {
    const defaultReason = action === "approve" ? "false positive after admin review" : "confirmed unsafe or not useful";
    openModal("Review note", `
      <div class="decision-form">
        <label for="decision-reason">Reason</label>
        <textarea id="decision-reason" class="decision-text">${escapeHTML(defaultReason)}</textarea>
        <div class="decision-actions">
          <button id="decision-cancel" type="button">Cancel</button>
          <button id="decision-submit" class="primary" type="button" data-icon="check">Save decision</button>
        </div>
      </div>
    `, () => resolve(null));
    byID<HTMLTextAreaElement>("decision-reason").focus();
    byID("decision-cancel").addEventListener("click", () => {
      state.modalOnClose = null;
      closeModal();
      resolve(null);
    });
    byID("decision-submit").addEventListener("click", () => {
      const reason = byID<HTMLTextAreaElement>("decision-reason").value.trim() || defaultReason;
      state.modalOnClose = null;
      closeModal();
      resolve(reason);
    });
    renderIcons();
  });
}

function selectedQuarantineRows(report: QuarantineReport) {
  const selected = Array.from(state.quarantineSelection);
  if (selected.length > 0) return selected;
  return report.rows.filter((row) => row.reason === "prompt_injection" && row.decision?.action !== "approve").map((row) => row.rowId);
}

function connectRunStream() {
  state.eventStream?.close();
  if (!state.selectedRun || state.selectedTab !== "events") return;
  state.eventStream = new EventSource(api.eventURL(`/admin/runs/${encodeURIComponent(state.selectedRun)}/events/stream`), {
    withCredentials: true
  });
  state.eventStream.addEventListener("run_event", () => {
    if (state.selectedTab === "events") void loadRunTab();
  });
}

async function loadLogs() {
  try {
    disconnectLogStream();
    state.logEvents = await api.events(logFilters());
    state.selectedLogEventID = state.logEvents[0]?.eventId || "";
    renderLogs();
  } catch (error) {
    state.logEvents = [];
    byID("logs-list").innerHTML = empty("Could not load events.");
    byID("log-count").textContent = "0 events";
    showCode("log-payload", error);
  }
}

async function connectLogStream() {
  disconnectLogStream();
  state.logEvents = await api.events(logFilters());
  state.selectedLogEventID = state.logEvents[0]?.eventId || "";
  renderLogs();
  const query = logQueryString();
  const path = `/admin/events/stream${query ? `?${query}` : ""}`;
  state.logStream = new EventSource(api.eventURL(path), { withCredentials: true });
  byID("log-stream-state").textContent = "live";
  state.logStream.addEventListener("run_event", (event) => {
    const runEvent = JSON.parse((event as MessageEvent).data) as RunEvent;
    if (!state.logEvents.some((item) => item.eventId === runEvent.eventId)) {
      state.logEvents = [runEvent, ...state.logEvents].slice(0, Number(logFilters().limit || "500"));
      state.selectedLogEventID ||= runEvent.eventId;
      renderLogs();
    }
  });
  state.logStream.onerror = () => {
    byID("log-stream-state").textContent = "reconnecting";
  };
}

function disconnectLogStream() {
  state.logStream?.close();
  state.logStream = null;
  const indicator = byID("log-stream-state");
  if (indicator) indicator.textContent = "idle";
}

async function loadNotifications() {
  state.notifications = await api.notifications({ limit: "200" });
  state.seenNotificationIds = new Set(state.notifications.map((notification) => notification.notificationId));
  renderNotifications();
  await loadNotificationCounts();
}

async function loadNotificationCounts() {
  state.notificationCounts = await api.notificationCounts();
  updateNotificationBadge();
}

function connectNotificationStream() {
  disconnectNotificationStream();
  if (!state.session) return;
  state.notificationStream = new EventSource(api.eventURL("/admin/notifications/stream?unread=true&limit=100"), {
    withCredentials: true
  });
  state.notificationStream.addEventListener("admin_notification", (event) => {
    const notification = JSON.parse((event as MessageEvent).data) as AdminNotification;
    const alreadySeen = state.seenNotificationIds.has(notification.notificationId);
    state.seenNotificationIds.add(notification.notificationId);
    state.notifications = [notification, ...state.notifications.filter((item) => item.notificationId !== notification.notificationId)].slice(0, 200);
    void loadNotificationCounts();
    if (!alreadySeen) playNotificationSound(notification);
    if (state.view === "notifications") renderNotifications();
  });
}

function disconnectNotificationStream() {
  state.notificationStream?.close();
  state.notificationStream = null;
}

function renderNotifications() {
  const list = byID("notifications-list");
  list.innerHTML = state.notifications.length ? "" : empty("No notifications.");
  for (const notification of state.notifications) list.appendChild(notificationItem(notification));
  byID("notification-count").textContent = `${state.notificationCounts.unread || 0} unread`;
  updateNotificationBadge();
  renderIcons();
}

function notificationItem(notification: AdminNotification) {
  const item = document.createElement("article");
  item.className = `notification-item ${notification.readAt ? "" : "unread"}`;
  const payload = notification.payload || {};
  const rows = Array.isArray(payload["rowIds"]) ? `${(payload["rowIds"] as unknown[]).length} row(s)` : "";
  item.innerHTML = `
    <div class="notification-main">
      <div class="notification-title">
        ${statusBadge(notification.severity || "info")}
        <strong>${escapeHTML(notification.title || notification.type)}</strong>
        <span>${escapeHTML(notification.createdAt || "-")}</span>
      </div>
      <p>${escapeHTML(notification.message || "-")}</p>
      <div class="notification-meta">
        <span>${escapeHTML(notification.libraryId || "-")}</span>
        <span>${escapeHTML(notification.runId || "-")}</span>
        <span>${escapeHTML(notification.type || "-")}</span>
        ${rows ? `<span>${escapeHTML(rows)}</span>` : ""}
      </div>
    </div>
    <div class="notification-actions">
      ${notification.runId ? `<button type="button" data-open-run data-icon="clock">Open run</button>` : ""}
      ${notification.readAt ? "" : `<button type="button" data-read data-icon="check">Mark read</button>`}
    </div>
  `;
  item.querySelector("[data-open-run]")?.addEventListener("click", () => void openNotificationRun(notification));
  item.querySelector("[data-read]")?.addEventListener("click", () => void markNotificationRead(notification.notificationId));
  return item;
}

async function openNotificationRun(notification: AdminNotification) {
  if (!notification.readAt) await markNotificationRead(notification.notificationId);
  if (notification.runId) await selectRun(notification.runId);
}

async function markNotificationRead(notificationID: string) {
  const updated = await api.markNotificationRead(notificationID);
  state.notifications = state.notifications.map((item) => (item.notificationId === notificationID ? updated : item));
  renderNotifications();
  await loadNotificationCounts();
}

async function markAllNotificationsRead() {
  state.notificationCounts = await api.markAllNotificationsRead();
  state.notifications = state.notifications.map((item) => ({ ...item, readAt: item.readAt || new Date().toISOString() }));
  renderNotifications();
}

function toggleNotificationSound() {
  state.notificationSound = !state.notificationSound;
  localStorage.setItem("oz_admin_notification_sound", state.notificationSound ? "true" : "false");
  renderNotificationSoundButton();
  if (state.notificationSound) playNotificationSound({ severity: "info" } as AdminNotification);
}

function renderNotificationSoundButton() {
  const button = byID("notification-sound");
  button.textContent = state.notificationSound ? "Sound on" : "Sound off";
  button.dataset.icon = state.notificationSound ? "volume2" : "volumex";
  button.querySelector("svg")?.remove();
  renderIcons();
}

function playNotificationSound(notification: AdminNotification) {
  if (!state.notificationSound) return;
  const AudioContextCtor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) return;
  const ctx = new AudioContextCtor();
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.type = "sine";
  oscillator.frequency.value = notification.severity === "error" ? 620 : 480;
  gain.gain.value = 0.045;
  oscillator.connect(gain);
  gain.connect(ctx.destination);
  oscillator.start();
  oscillator.stop(ctx.currentTime + 0.18);
  window.setTimeout(() => void ctx.close(), 260);
}

function updateNotificationBadge() {
  const nav = document.querySelector<HTMLButtonElement>('[data-view="notifications"]');
  if (!nav) return;
  let badge = nav.querySelector<HTMLSpanElement>(".nav-count");
  const unread = state.notificationCounts.unread || 0;
  if (!unread) {
    badge?.remove();
    byID("notification-count").textContent = "0 unread";
    return;
  }
  if (!badge) {
    badge = document.createElement("span");
    badge.className = "nav-count";
    nav.appendChild(badge);
  }
  badge.textContent = unread > 99 ? "99+" : String(unread);
  byID("notification-count").textContent = `${unread} unread`;
}

async function applyLogQuickFilter(filter: string) {
  byID<HTMLInputElement>("log-run-id").value = "";
  byID<HTMLInputElement>("log-source-id").value = "";
  byID<HTMLInputElement>("log-library-id").value = "";
  byID<HTMLInputElement>("log-event-type").value = "";
  byID<HTMLSelectElement>("log-stage").value = "";
  byID<HTMLSelectElement>("log-level").value = "";
  if (filter === "errors") {
    byID<HTMLSelectElement>("log-level").value = "error";
  } else if (filter === "publish") {
    byID<HTMLInputElement>("log-event-type").value = "publish_gate_failed";
  } else if (filter === "discover") {
    byID<HTMLSelectElement>("log-stage").value = "discover";
  }
  await loadLogs();
}

function logFilters() {
  return {
    runId: byID<HTMLInputElement>("log-run-id").value,
    sourceId: byID<HTMLInputElement>("log-source-id").value,
    libraryId: byID<HTMLInputElement>("log-library-id").value,
    stage: byID<HTMLSelectElement>("log-stage").value,
    level: byID<HTMLSelectElement>("log-level").value,
    eventType: byID<HTMLInputElement>("log-event-type").value,
    limit: byID<HTMLSelectElement>("log-limit").value
  };
}

function logQueryString() {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(logFilters())) {
    if (value.trim()) params.set(key, value.trim());
  }
  return params.toString();
}

function renderLogs() {
  const list = byID("logs-list");
  byID("log-count").textContent = `${state.logEvents.length} events`;
  list.innerHTML = state.logEvents.length ? "" : empty("No events match the filters.");
  for (const event of state.logEvents) list.appendChild(logItem(event));
  const selected = state.logEvents.find((event) => event.eventId === state.selectedLogEventID) || state.logEvents[0];
  byID("log-payload").textContent = selected ? JSON.stringify(selected, null, 2) : "Select an event.";
}

function logItem(event: RunEvent) {
  const item = document.createElement("button");
  item.type = "button";
  item.className = `log-item ${event.eventId === state.selectedLogEventID ? "active" : ""}`;
  const summary = logPayloadSummary(event.payload);
  item.innerHTML = `
    <div class="log-row-head">
      ${statusBadge(event.level || "info")}
      <strong>${escapeHTML(event.eventType || "-")}</strong>
      <span>${escapeHTML(event.createdAt || "-")}</span>
    </div>
    <p>${escapeHTML(event.message || "-")}</p>
    ${summary ? `<p class="log-summary">${escapeHTML(summary)}</p>` : ""}
    <div class="log-meta">
      <span>${escapeHTML(event.stage || "-")}</span>
      <span>${escapeHTML(event.libraryId || "-")}</span>
      <span>${escapeHTML(event.runId || "-")}</span>
      <span>${escapeHTML(event.sourceId || "-")}</span>
    </div>
  `;
  item.addEventListener("click", () => {
    state.selectedLogEventID = event.eventId;
    renderLogs();
  });
  return item;
}

function logPayloadSummary(payload?: Record<string, unknown>) {
  if (!payload) return "";
  const parts: string[] = [];
  for (const key of ["tier", "score", "runs", "rows", "unitCount", "aggregateId", "keywordIndexPath"]) {
    const value = payload[key];
    if (value === undefined || value === null || value === "") continue;
    parts.push(`${key}: ${String(value)}`);
  }
  return parts.slice(0, 4).join(" · ");
}

function switchView(view: ViewName, options: { syncRoute?: boolean; load?: boolean; replace?: boolean } = {}) {
  state.view = view;
  if (view !== "logs") disconnectLogStream();
  if (options.syncRoute !== false) {
    const route = routeForView(view);
    const method = options.replace ? "replaceState" : "pushState";
    window.history[method]({}, "", route);
  }
  document.querySelectorAll("[data-view]").forEach((node) => node.classList.toggle("active", (node as HTMLElement).dataset.view === view));
  document.querySelectorAll(".view").forEach((node) => node.classList.toggle("active", node.id === `view-${view}`));
  if (options.load === false) return;
  void loadVisibleViewData(view);
}

function switchLibraryWorkspace(workspace: LibraryWorkspace) {
  state.libraryWorkspace = workspace || "catalog";
  document.querySelectorAll<HTMLElement>("[data-library-workspace]").forEach((node) => {
    node.classList.toggle("active", node.dataset.libraryWorkspace === state.libraryWorkspace);
  });
  document.querySelectorAll<HTMLElement>(".library-workspace-panel").forEach((node) => {
    node.classList.toggle("hidden", node.id !== `library-workspace-${state.libraryWorkspace}`);
  });
  if (state.libraryWorkspace === "bulkJobs") void loadBulkJobs();
  renderIcons();
}

function switchRunTab(tab: RunTab) {
  state.selectedTab = tab;
  document.querySelectorAll("[data-run-tab]").forEach((node) => node.classList.toggle("active", (node as HTMLElement).dataset.runTab === tab));
  connectRunStream();
  void loadRunTab();
}

function libraryItem(profile: LibraryProfile) {
  const item = document.createElement("article");
  item.className = "list-item clickable";
  item.innerHTML = `
    <div>
      <strong>${escapeHTML(profile.displayName)}</strong>
      <p>${escapeHTML(profile.libraryId)} · ${escapeHTML(profile.defaultVersion || "-")}</p>
      <p>${escapeHTML(unitCountLabel(profile))} · ${formatCount(profile.tokenCount || 0)} tokens · ${escapeHTML(profile.lastPublishedAt || profile.updatedAt || "-")}</p>
    </div>
    ${statusBadge(profile.status)}
  `;
  item.addEventListener("click", () => void selectLibrary(profile.libraryId));
  return item;
}

function sourceItem(record: SourceRecord) {
  const source = record.source;
  const ref = source.source_type === "github_repo" ? source.version_policy?.default_branch || "-" : source.websiteBaseUrl || source.url;
  const item = document.createElement("article");
  item.className = "list-item";
  item.innerHTML = `
    <div>
      <strong>${escapeHTML(source.library_id || source.source_id)}</strong>
      <p>${escapeHTML(source.source_type)} · ${escapeHTML(source.url)}</p>
      <p>ref ${escapeHTML(ref)} · last ${escapeHTML(record.lastRunId || "-")} · active ${escapeHTML(record.activeRunId || "-")}</p>
    </div>
    <div class="item-actions">
      ${statusBadge(record.lastStatus || "unknown")}
      <button type="button" data-refresh data-icon="refresh">Refresh</button>
      <button type="button" data-force data-icon="rotate">Force</button>
    </div>
  `;
  item.querySelector("[data-refresh]")!.addEventListener("click", () => void refreshSource(source, false));
  item.querySelector("[data-force]")!.addEventListener("click", () => void refreshSource(source, true));
  return item;
}

function renderRefreshTiers() {
  const root = document.getElementById("refresh-tier-report");
  if (!root) return;
  const query = state.refreshTierSearch.trim().toLowerCase();
  const usageByLibrary = monthlyLibraryUsage();
  const filtered = state.sources
    .filter((record) => matchesRefreshTierSearch(record, query))
    .sort((left, right) => {
      const leftUsage = usageByLibrary.get(sourceUsageKey(left)) || 0;
      const rightUsage = usageByLibrary.get(sourceUsageKey(right)) || 0;
      return rightUsage - leftUsage || (left.source.library_id || "").localeCompare(right.source.library_id || "");
    });
  const groups: Record<RefreshTier, SourceRecord[]> = { high: [], normal: [], low: [] };
  for (const record of filtered) groups[normalizeRefreshTier(record.source.refresh_policy?.priority)].push(record);
  root.innerHTML = `
    ${refreshTierConfigPanel()}
    <div class="refresh-tier-layout">
      ${(["high", "normal", "low"] as RefreshTier[]).map((tier) => refreshTierColumn(tier, groups[tier], usageByLibrary)).join("")}
    </div>
  `;
  root.querySelectorAll<HTMLSelectElement>("[data-refresh-tier-select]").forEach((select) => {
    select.addEventListener("change", () => {
      const sourceID = select.dataset.sourceId || "";
      const record = state.sources.find((row) => row.source.source_id === sourceID);
      if (!record) return;
      void updateSourceRefreshTier(record, select.value as RefreshTier);
    });
  });
  root.querySelectorAll<HTMLButtonElement>("[data-refresh-tier-refresh]").forEach((button) => {
    button.addEventListener("click", () => {
      const sourceID = button.dataset.sourceKey || "";
      const record = state.sources.find((row) => row.source.source_id === sourceID);
      if (!record) return;
      void refreshSource(record.source, false);
    });
  });
  renderIcons();
}

function refreshTierConfigPanel() {
  const config = state.systemConfig || {};
  const rows = [
    ["Hot", `${configNumber(config.refreshHotHours, 24)}h`, "manual high-priority sources"],
    ["Warm", `${configNumber(config.refreshWarmDays, 5)}d`, `auto after ${configNumber(config.refreshWarmThreshold, 10)} docs requests`],
    ["Cold", `${configNumber(config.refreshColdDays, 10)}d`, "default low-priority sources"],
    ["Default", `${configNumber(config.refreshAfterDays, 10)}d`, "fallback interval"],
    ["Scheduler", String(config.schedulerSpec || "@every 12h"), "heartbeat only"],
    ["Max / tick", String(configNumber(config.schedulerMaxPerTick, 25)), "sources per scheduler run"],
    ["Schema migrations", String(configNumber(config.schedulerMigrationMax, 2)), "migration cap per tick"],
    ["Failure backoff", `${configNumber(config.schedulerFailureBackoffHours, 6)}h`, "multiplied by failure count"]
  ];
  return `
    <section class="panel refresh-tier-config">
      ${rows.map(([label, value, hint]) => `
        <div>
          <span>${escapeHTML(label)}</span>
          <strong>${escapeHTML(value)}</strong>
          <p>${escapeHTML(hint)}</p>
        </div>
      `).join("")}
    </section>
  `;
}

function refreshTierColumn(tier: RefreshTier, records: SourceRecord[], usageByLibrary: Map<string, number>) {
  return `
    <section class="panel refresh-tier-column">
      <div class="panel-head">
        <div>
          <h2>${refreshTierLabels[tier]}</h2>
          <p class="muted">${refreshTierHint(tier)}</p>
        </div>
        <span class="count-pill">${records.length}</span>
      </div>
      <div class="refresh-tier-list">
        ${records.map((record) => refreshTierRow(record, usageByLibrary.get(sourceUsageKey(record)) || 0)).join("") || empty(`No ${refreshTierLabels[tier].toLowerCase()} sources.`)}
      </div>
    </section>
  `;
}

function refreshTierRow(record: SourceRecord, monthlyRequests: number) {
  const source = record.source;
  const currentTier = normalizeRefreshTier(source.refresh_policy?.priority);
  const ref = source.source_type === "github_repo" ? source.version_policy?.default_branch || "-" : source.websiteBaseUrl || source.url;
  const meta = [
    source.source_type,
    `next ${record.nextRefreshAfter || "-"}`,
    record.pendingRefresh ? `pending${record.pendingReason ? `: ${record.pendingReason}` : ""}` : "",
    record.failureCount ? `${record.failureCount} failures` : ""
  ].filter(Boolean).join(" · ");
  return `
    <article class="refresh-tier-row">
      <div class="refresh-tier-main">
        <strong>${escapeHTML(source.library_id || source.source_id)}</strong>
        <p>${escapeHTML(ref)}</p>
        <p>${escapeHTML(meta)}</p>
      </div>
      <div class="refresh-tier-actions">
        <span>${formatCount(monthlyRequests)} req/mo</span>
        <select data-refresh-tier-select data-source-id="${escapeHTML(source.source_id)}">
          ${refreshPriorityOptions(currentTier)}
        </select>
        <button type="button" data-refresh-tier-refresh data-source-key="${escapeHTML(source.source_id)}" data-icon="refresh">Refresh</button>
      </div>
    </article>
  `;
}

function refreshTierHint(tier: RefreshTier) {
  if (tier === "high") return "24h refresh for important sources.";
  if (tier === "normal") return "5d refresh for active sources.";
  return "10d refresh for quiet sources.";
}

function normalizeRefreshTier(value?: string): RefreshTier {
  if (value === "hot" || value === "high") return "high";
  if (value === "cold" || value === "low") return "low";
  return "normal";
}

function matchesRefreshTierSearch(record: SourceRecord, query: string) {
  if (!query) return true;
  const source = record.source;
  return [
    source.library_id,
    source.source_id,
    source.url,
    source.websiteBaseUrl,
    source.source_type,
    record.lastStatus,
    record.pendingReason
  ].filter(Boolean).join(" ").toLowerCase().includes(query);
}

function monthlyLibraryUsage() {
  const usage = new Map<string, number>();
  for (const window of state.usage?.windows || []) {
    if (window.scopeType !== "library" || window.windowKind !== "month") continue;
    const key = normalizeLibraryKey(window.libraryId || window.scopeId);
    usage.set(key, (usage.get(key) || 0) + (window.requests || 0));
  }
  return usage;
}

function sourceUsageKey(record: SourceRecord) {
  return normalizeLibraryKey(record.source.library_id || record.source.source_id);
}

function normalizeLibraryKey(value: string) {
  return value.replace(/^\/+/, "").toLowerCase();
}

function configNumber(value: unknown, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

async function updateSourceRefreshTier(record: SourceRecord, tier: RefreshTier) {
  try {
    await api.updateLibrarySource(record.source.library_id, record.source.source_id, { refreshPriority: tier });
    await loadRefreshTiers();
  } catch (error) {
    openModal("Refresh tier update failed", `<p>${escapeHTML(error instanceof Error ? error.message : String(error))}</p>`);
    renderRefreshTiers();
  }
}

function librarySourceManagerItem(libraryID: string, record: SourceRecord) {
  const source = record.source;
  const id = cssID(source.source_id);
  const isGitHub = source.source_type === "github_repo";
  const ref = isGitHub ? source.version_policy?.default_branch || "-" : source.websiteBaseUrl || source.url;
  const item = document.createElement("article");
  item.className = "source-card";
  item.innerHTML = `
    <div class="source-card-head">
      <div>
        <strong>${escapeHTML(source.source_type)} · ${escapeHTML(source.source_id)}</strong>
        <p>${escapeHTML(source.url)} · ref ${escapeHTML(ref)}</p>
      </div>
      <div class="item-actions">
        ${statusBadge(source.disabled ? "disabled" : record.lastStatus || "unknown")}
        <button type="button" data-save data-icon="check">Save</button>
        <button type="button" data-refresh data-icon="refresh">Refresh</button>
        <button type="button" data-force data-icon="rotate">Force</button>
      </div>
    </div>
    <div class="source-card-grid">
      <label>Role<select id="source-role-${id}">
        ${sourceRoleOptions(source.source_role || defaultRoleForSourceType(source.source_type))}
      </select></label>
      <label>Priority<input id="source-priority-${id}" type="number" min="1" value="${source.aggregate_priority || ""}" /></label>
      <label>Refresh tier<select id="source-refresh-priority-${id}">
        ${refreshPriorityOptions(source.refresh_policy?.priority || "normal")}
      </select></label>
      <label>Website base URL<input id="source-base-${id}" value="${escapeHTML(source.websiteBaseUrl || "")}" /></label>
      <label class="check-row"><input id="source-disabled-${id}" type="checkbox" ${source.disabled ? "checked" : ""} /> Disabled</label>
      <label class="check-row"><input id="source-exclude-agg-${id}" type="checkbox" ${source.exclude_from_aggregate ? "checked" : ""} /> Exclude from aggregate</label>
      <label>Include<textarea id="source-include-${id}" class="short-text">${escapeHTML((source.crawl_scope.include || []).join("\n"))}</textarea></label>
      <label>Exclude<textarea id="source-exclude-${id}" class="short-text">${escapeHTML((source.crawl_scope.exclude || []).join("\n"))}</textarea></label>
    </div>
  `;
  item.querySelector("[data-save]")!.addEventListener("click", () => void saveLibrarySource(libraryID, source.source_id));
  item.querySelector("[data-refresh]")!.addEventListener("click", () => void refreshLibrarySource(libraryID, source.source_id, false));
  item.querySelector("[data-force]")!.addEventListener("click", () => void refreshLibrarySource(libraryID, source.source_id, true));
  return item;
}

function sourceRoleOptions(selected: string) {
  return [
    ["primary_docs", "Primary docs"],
    ["website_docs", "Website docs"],
    ["llms_manifest", "LLMS manifest"],
    ["api_reference", "API reference"],
    ["manual_docs", "Manual docs"],
    ["docs", "Docs"]
  ]
    .map(([value, label]) => `<option value="${value}" ${selected === value ? "selected" : ""}>${label}</option>`)
    .join("");
}

function refreshPriorityOptions(selected: string) {
  const normalized = selected === "hot" || selected === "high"
    ? "high"
    : selected === "cold" || selected === "low"
      ? "low"
      : "normal";
  return [
    ["high", "Hot · 24h"],
    ["normal", "Warm · 5d"],
    ["low", "Cold · 10d"]
  ]
    .map(([value, label]) => `<option value="${value}" ${normalized === value ? "selected" : ""}>${label}</option>`)
    .join("");
}

function aggregateView(profile: LibraryProfile, aggregate: PublishedAggregate) {
  const rows = aggregate.sourceRuns || [];
  return `
    <div class="aggregate-head">
      <div class="detail-grid">
        ${fieldView("Aggregate ID", aggregate.aggregateId || "-")}
        ${fieldView("Version", aggregate.version || profile.defaultVersion || "-")}
        ${fieldView("Sources", String(aggregate.sourceIds?.length || 0))}
        ${fieldView("Runs", String(aggregate.runIds?.length || 0))}
        ${fieldView("Units", String(aggregate.unitCount || 0))}
        ${fieldView("Tokens", String(aggregate.tokenCount || 0))}
        ${fieldView("Deduped", String(aggregate.dedupeCount || 0))}
        ${fieldView("Published", aggregate.publishedAt || "-")}
      </div>
      <div class="actions padded">
        <button id="rebuild-aggregate" class="primary" type="button" data-icon="refresh">Rebuild aggregate</button>
      </div>
    </div>
    <div class="section-title">Source runs in aggregate</div>
    <div class="list">
      ${
        rows.length
          ? rows.map((row) => `
            <article class="list-item">
              <div>
                <strong>${escapeHTML(row.sourceRole || row.sourceType || row.sourceId)}</strong>
                <p>${escapeHTML(row.sourceId)} · ${escapeHTML(row.runId)} · priority ${row.priority || 100}</p>
                <p>${row.unitCount || 0} units · ${row.tokenCount || 0} tokens · ${escapeHTML(row.publishedAt || "-")}</p>
              </div>
              ${statusBadge(row.active ? "active" : "inactive")}
            </article>
          `).join("")
          : empty("No source runs in this aggregate.")
      }
    </div>
  `;
}

function runItem(run: RunState) {
  const item = document.createElement("article");
  item.className = "list-item clickable";
  const selectable = isCancelableRun(run);
  item.innerHTML = `
    <div class="run-row">
      <input class="run-checkbox" type="checkbox" aria-label="Select run" ${state.selectedRunIDs.has(run.runId) ? "checked" : ""} ${selectable ? "" : "disabled"} />
      <div class="run-main">
        <strong>${escapeHTML(run.libraryId || run.runId)}</strong>
        <p>${escapeHTML(run.runId)} · ${escapeHTML(run.version || "-")}</p>
        <p>${escapeHTML(run.sourceId || "-")} · updated ${escapeHTML(run.updatedAt || run.createdAt || "-")}</p>
        ${run.progress ? progressBlock(run.progress) : ""}
      </div>
    </div>
    ${statusBadge(run.status)}
  `;
  item.querySelector<HTMLInputElement>(".run-checkbox")?.addEventListener("click", (event) => {
    event.stopPropagation();
    if (!selectable) return;
    const checkbox = event.currentTarget as HTMLInputElement;
    if (checkbox.checked) state.selectedRunIDs.add(run.runId);
    else state.selectedRunIDs.delete(run.runId);
  });
  item.addEventListener("click", () => void selectRun(run.runId));
  return item;
}

function isCancelableRun(run: RunState) {
  return run.status === "queued" || run.status === "running";
}

function progressBlock(progress: NonNullable<RunState["progress"]>) {
  const percent = progressPercent(progress);
  return `
    <div class="run-progress">
      <div class="run-progress-meta">
        <span>${escapeHTML(progressText(progress))}</span>
        <span>${percent.toFixed(0)}%</span>
      </div>
      <div class="run-progress-bar" aria-hidden="true"><span style="width:${percent}%"></span></div>
      <div class="run-progress-foot">
        <span>${escapeHTML(progressEndpointText(progress))}</span>
        <span>${escapeHTML(formatETA(progress.etaSeconds || 0))}</span>
      </div>
    </div>
  `;
}

function progressPercent(progress: NonNullable<RunState["progress"]>) {
  const raw = Number(progress.percent || 0);
  if (Number.isFinite(raw) && raw > 0) return Math.max(0, Math.min(100, raw));
  if (!progress.unitsTotal) return 0;
  return Math.max(0, Math.min(100, ((progress.unitsProcessed || 0) / progress.unitsTotal) * 100));
}

function progressText(progress: NonNullable<RunState["progress"]>) {
  const stage = progress.stage || "embed";
  if (!progress.unitsTotal) return progress.message || `${stage} queued`;
  const pending = progress.unitsPending || 0;
  const batches = progress.batchesPending || 0;
  return `${stage}: ${progress.unitsProcessed || 0}/${progress.unitsTotal} units · ${pending} pending · ${batches} batches`;
}

function progressEndpointText(progress: NonNullable<RunState["progress"]>) {
  const endpoints = progress.activeEndpoints || [];
  const endpoint = endpoints.length ? `${endpoints.length} endpoint${endpoints.length === 1 ? "" : "s"}` : "no endpoint yet";
  const cache = (progress.cacheHits || progress.cacheMisses) ? ` · cache ${progress.cacheHits || 0}/${(progress.cacheHits || 0) + (progress.cacheMisses || 0)}` : "";
  return `${progress.provider || "embedder"}${progress.model ? ` · ${progress.model}` : ""} · ${endpoint}${cache}`;
}

function formatETA(seconds: number) {
  if (!seconds || seconds <= 0) return "ETA -";
  if (seconds < 60) return `ETA ${seconds}s`;
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `ETA ${minutes}m`;
  return `ETA ${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

function versionItem(libraryID: string, version: LibraryVersion) {
  const label = version.label || version.title || version.ref;
  const refType = version.refType || version.type || "-";
  const item = document.createElement("article");
  item.className = "list-item";
  item.innerHTML = `
    <div>
      <strong>${escapeHTML(label)}</strong>
      <p>${escapeHTML(refType)} · ${escapeHTML(version.ref || "-")}${version.isDefault ? " · default" : ""}</p>
      <p>${escapeHTML(version.runId || "-")} · ${escapeHTML(version.publishedAt || version.lastIndexedAt || version.updatedAt || "-")}</p>
      <p>${escapeHTML(unitCount(version) ? unitCountLabel(version) : "no units yet")}${version.error ? ` · ${escapeHTML(version.error)}` : ""}</p>
    </div>
    <div class="item-actions">
      ${statusBadge(version.status || "configured")}
      <button type="button" data-validate data-icon="check">Validate</button>
      <button type="button" data-refresh data-icon="refresh">Refresh</button>
      <button type="button" data-delete data-icon="trash">Delete</button>
    </div>
  `;
  item.querySelector("[data-validate]")?.addEventListener("click", () => void validateVersion(libraryID, version.versionId));
  item.querySelector("[data-refresh]")?.addEventListener("click", () => void refreshVersion(libraryID, version.versionId));
  item.querySelector("[data-delete]")?.addEventListener("click", () => void deleteVersion(libraryID, version.versionId));
  return item;
}

function deadLetterItem(item: DeadLetter) {
  const row = document.createElement("article");
  row.className = "list-item";
  row.innerHTML = `
    <div>
      <strong>${escapeHTML(item.job.jobId || "-")}</strong>
      <p>${escapeHTML(item.job.runId || "-")} · ${escapeHTML(item.job.stage || "-")} · attempts ${item.attempts || 0}</p>
      <p>${escapeHTML(item.error || "-")}</p>
    </div>
    <button type="button" data-icon="rotate">Requeue</button>
  `;
  row.querySelector("button")!.addEventListener("click", async () => {
    showCode("system-output", await api.requeueDeadLetter(item.job.jobId));
    await loadDeadLetters();
  });
  return row;
}

function openModal(title: string, body: string, onClose?: () => void) {
  state.modalOnClose = onClose || null;
  byID("modal-title").textContent = title;
  byID("modal-body").innerHTML = body;
  byID("modal").classList.remove("hidden");
  renderIcons();
}

function closeModal() {
  const onClose = state.modalOnClose;
  state.modalOnClose = null;
  byID("modal").classList.add("hidden");
  byID("modal-body").innerHTML = "";
  onClose?.();
}

function showDetailCode(value: unknown) {
  byID("library-body").innerHTML = `<pre class="code tall">${escapeHTML(value instanceof Error ? value.message : JSON.stringify(value, null, 2))}</pre>`;
}

function showCode(id: string, value: unknown) {
  const element = byID(id);
  element.classList.remove("quarantine-output");
  element.textContent = value instanceof Error ? value.message : JSON.stringify(value, null, 2);
}

function showCodeElement(element: HTMLElement, value: unknown) {
  element.innerHTML = `<pre class="code compact">${escapeHTML(value instanceof Error ? value.message : JSON.stringify(value, null, 2))}</pre>`;
}

function isUnauthorized(error: unknown) {
  return /unauthorized/i.test(error instanceof Error ? error.message : String(error));
}

function renderIcons() {
  const iconMap = {
    alert: AlertTriangle,
    ban: Ban,
    bell: Bell,
    book: BookOpen,
    check: Check,
    clock: Clock,
    database: Database,
    file: FileText,
    play: Play,
    refresh: RefreshCcw,
    rotate: RotateCcw,
    search: Search,
    server: Server,
    settings: Settings,
    trash: Trash2,
    upload: Upload,
    userplus: UserPlus,
    volume2: Volume2,
    volumex: VolumeX,
    logout: LogOut
  };
  document.querySelectorAll<HTMLElement>("[data-icon]").forEach((node) => {
    if (node.querySelector("svg")) return;
    const name = node.dataset.icon as keyof typeof iconMap;
    const Icon = iconMap[name];
    if (!Icon) return;
    node.prepend(createElement(Icon, { width: "16", height: "16", "stroke-width": "1.8" }));
  });
}

function navButton(view: ViewName, label: string, icon: string) {
  return `<button type="button" class="nav ${view === "libraries" ? "active" : ""}" data-view="${view}" data-icon="${icon}">${label}</button>`;
}

function libraryWorkspaceButton(workspace: LibraryWorkspace, label: string, icon: string) {
  return `<button type="button" class="workspace-tab ${workspace === "catalog" ? "active" : ""}" data-library-workspace="${workspace}" data-icon="${icon}">${label}</button>`;
}

function libraryTabButton(tab: LibraryTab, label: string) {
  return `<button type="button" class="tab ${tab === "overview" ? "active" : ""}" data-library-tab="${tab}">${label}</button>`;
}

function runTabButton(tab: RunTab, label: string) {
  return `<button type="button" class="tab ${tab === "events" ? "active" : ""}" data-run-tab="${tab}">${label}</button>`;
}

function fieldView(label: string, value: string) {
  return `<div class="field-view"><span>${escapeHTML(label)}</span><strong>${escapeHTML(value)}</strong></div>`;
}

function summaryCell(label: string, value: string) {
  return `<div><span>${escapeHTML(label)}</span><strong>${value}</strong></div>`;
}

function statusBadge(status: string) {
  return `<span class="status ${escapeHTML(status)}">${escapeHTML(status || "unknown")}</span>`;
}

function empty(text: string) {
  return `<p class="empty">${escapeHTML(text)}</p>`;
}

function linesFrom(id: string) {
  return byID<HTMLTextAreaElement>(id).value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
}

function numberValue(id: string) {
  const value = Number(byID<HTMLInputElement>(id).value);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

function cssID(value: string) {
  return value.replace(/[^A-Za-z0-9_-]/g, "_");
}

function byID<T extends HTMLElement = HTMLElement>(id: string): T {
  return document.getElementById(id) as T;
}

function escapeHTML(value: unknown) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
